/* eslint-disable react/destructuring-assignment */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Column, Pie } from "@ant-design/plots";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  Alert,
  Button,
  DatePicker,
  Dropdown,
  Image,
  message,
  Pagination,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { navigate } from "gatsby";
import { capitalize } from "lodash";
import { LuCircleX, LuColumns3, LuDownload, LuEllipsis, LuSettings } from "react-icons/lu";
import { useDispatch } from "react-redux";
import {
  GET_APPOINTMENT_ANALYSIS,
  GET_OPPORTUNITY_STATUS_NEW,
  GET_ACTIVITIES_BY_SALES_PERSON,
  GET_APPOINTMENTS_BY_SALES_PERSON,
  GET_MESSAGES_BY_SALES_PERSON,
  GET_OPPORTUNITY_BY_SALES_PERSON,
  EXPORT_DATA_BY_SOURCE,
  GET_DASHBOARD_ANALYTICS,
  GET_DATA_BY_SOURCE,
  GET_ENGAGEMENT_ANALYTICS,
  GET_LEAD_CONTECTED_BY_SOURCE,
  GET_LEAD_ENGAGEMENT_ANALYSIS,
  GET_REVIEW_STATUS_ANALYSIS,
  GET_USER_MESSAGE_ANALYSIS,
  GET_USER_MESSAGE_RESPONSE_TIME_ANALYSIS,
  GET_USER_MESSAGE_TYPE_ANALYSIS,
  GET_USER_REVIEW_ANALYSIS,
} from "../../../graphql/query";
import useLang from "../../../hooks/useLang";
import useOpportunityTypeTree from "../../../hooks/useOpportunityTypeTree";
import { DashboardArrow } from "../../../icons/js/DashboardArrow";
import { DownIcon } from "../../../icons/js/DownIcon";
import { EngagementVector } from "../../../icons/js/EngagementSVG";
import CollapseIcon from "../../../icons/svg/Collapse.svg";
import { isAllLocationSelected, openDrawer } from "../../../library/helpers";
import { SORT_DIRECTION } from "../../../library/antdHelpers";
import { DRAWER_TYPES } from "../../hoc/Drawer/drawer";
import { parseVehicle } from "../../../library/utils";
import {
  setQuickFilters,
  setUserFilters,
  setTeamFilters,
  setOpportunityTypeFilters,
  setOpportunityTypeFilterCount,
  setDateFilterType,
  setLeadDateRange,
  setLeadDateType,
  setCameFromDashboard,
  resetLeadFilters,
  setSelectedLeadRedux,
  QUICK_FILTERS,
  DEFAULT_DATE_FILTER_TYPE,
} from "../../../redux/slices/leadSlice";
import { leadStatus as LEAD_STATUS_OPTIONS } from "../activityCenter/engagementActivity";
import MessageTypeDetailDrawer from "./MessageTypeDetailDrawer";
import SourceExportPreferenceDrawer from "./SourceExportPreferenceDrawer";
import { useDashboardFilter } from "../../../context/DashboardFilterContext";
import { COMMUNICATION_DETAIL_TABS, ROUTES, navigateWithQuery } from "../../../Routes";
import { setModuleFilters } from "../../../redux/slices/persistedFiltersSlice";
import {
  MESSAGE_REPORT_SOURCE,
  MESSAGE_DETAIL_MODULE,
  SALES_MSG_COL_TO_DETAIL,
  resolveDetailSelectionFromMetric,
} from "./engagementDashboardHelpers";
import { COMMON_PAGINATION, REVIEW_STATUS } from "../../../library/constants";
import {
  CUSTOM as APPOINTMENT_DATE_CUSTOM,
  reducerSetAppointmentFilters,
  APPOINTMENT_DATE_MODE,
} from "../../../redux/slices/appointmentSlice";
import {
  setOpportunityFilters,
  setForceOpportunityView,
} from "../../../redux/slices/opportunitySlice";
import CustomTable from "../../commonComponents/table";
import { CUSTOM } from "../../commonComponents/DateRangeFilter";
import { APPOINTMENT_STATUSES } from "../../../constant/OttoAnalyticsConstants";
import DateBasisDropdown, { DATE_BASIS } from "../../commonComponents/DateBasisDropdown";
import OpportunitySourceSelect from "../../commonComponents/OpportunitySourceSelect";
import OpportunityDateBasisDropdown, {
  OPPORTUNITY_DATE_BASIS,
} from "../../commonComponents/OpportunityDateBasisDropdown";
import StatusColumnFilter from "../../commonComponents/StatusColumnFilter";
import CustomAvatar from "../../commonComponents/CustomAvatar";
import CheckoutReportUserLink from "./CheckoutReportUserLink";
import useOpportunitySources from "../../../hooks/useOpportunitySources";
import {
  activityAnalysis,
  appointmentsAnalytics,
  convertMinutesToHour,
  convertToTitleCase,
  filterByRange,
  filterByStatus,
  LAST_MONTH,
  LAST_WEEK,
  leadBySource,
  THIS_MONTH,
  LAST_30_DAYS,
  THIS_WEEK,
  VIEW_TYPES,
  VIEW_MODES,
  ORDER_BY_FIELDS,
} from "./dashboardHelpers";
import {
  NEW_LEADS,
  ENGAGED_LEADS,
  ENGAGEMENT_RATE,
  MESSAGES_SENT_VS_RECEIVED,
  SENT_MESSAGES,
  RECEIVED_MESSAGES,
  NEW_LEAD_APPOINTMENT,
  BOOKED_APPOINTMENTS,
  BOOKED_APPOINTMENT_RATE,
  UNCONTACTED,
  NOT_RESPONDED,
  RESPONDED,
  NOT_ENGAGED,
  ENGAGED,
  WAITING_FOR_LEAD_REPLY,
  NEEDS_YOUR_REPLY,
  NO_RESPONSE_NEEDED,
  AVG_RESPONSE_TIME,
  AVG_RESPONSE_TIME_AFTER_NUDGE,
  INVITE_SENT,
  REVIEW_COMPLETED,
  INVITE_NOT_SENT,
  APPOINTMENTS_BY_SALES_PERSON,
  ACTIVITY_BY_SALES_PERSON,
  COMMUNICATION_BY_SALES_PERSON,
  OPPORTUNITY_BY_SALES_PERSON,
  LEAD_ENGAGEMENT as LEAD_ENGAGEMENT_INFO,
  SOURCE_GRAPH,
  SOURCE_GRAPH_MESSAGE,
  MESSAGE_TYPE as MESSAGE_TYPE_INFO,
  RESPONSE_TIME as RESPONSE_TIME_INFO,
  REVIEW_REQUEST_ANALYSIS as REVIEW_REQUEST_ANALYSIS_INFO,
  REVIEW_STATUS as REVIEW_STATUS_INFO,
  APPOINTMENT_ANALYSIS as APPOINTMENT_ANALYSIS_INFO,
} from "./DashboardMetricContent";
import GraphAnalysis from "./graphAnalysis";
import CustomButton from "../../commonComponents/button";
import { downloadFileFromUrl } from "../../../utils/mediaDownloader";
import { EXPORT_FORMAT } from "../../commonComponents/schedule/scheduleHelpers";
import ColumnVisibilityDrawer from "../../callDashboard/ColumnVisibilityDrawer";
import MetricHeader, { SubMetricInfoIcon } from "./MetricHeader";
import {
  DATE_TIME_FORMATS,
  formatResponseTime,
  formatFilterDateRangeLabel,
  formatFilterDateRangeOnly,
  formatFilterLabelOnly,
} from "../../../utils/dateTimes";
import useLeadRedirection from "../../../hooks/useLeadRedirection";

// Module key used to hand off filter overrides (e.g. a user-row click) from
// the dashboard to the detail page via Redux instead of URL params.
export const MEDIA_TRACKING_REPORT_MODULE = "mediaTrackingDetailReport";

// Navigates to the message-detail page with `?source=...` in the URL
// (drives the header). Any extra preset filters (e.g. opportunitySourceIds)
// are handed off via persistedFilters so the URL stays clean. Detail page
// consumes the preset on mount and clears it.
const goToMessageDetail = (source, dispatch, extraFilters) => {
  if (extraFilters && Object.keys(extraFilters).length > 0) {
    dispatch(
      setModuleFilters({
        moduleKey: MESSAGE_DETAIL_MODULE,
        filters: extraFilters,
      })
    );
  }
  navigateWithQuery(ROUTES.messageSentReceivedDetailReport, { source }, navigate);
};

// Pulls the human-readable label for a QUICK_FILTERS id straight from the
// activity-center quick-filter list. Keeps Lead Engagement column titles +
// summary box labels in lock-step with the AC filter panel labels.
const labelForQuickFilter = id => LEAD_STATUS_OPTIONS.find(s => s.id === id)?.name || id;

/**
 *
 * @param {{filterType:string, handleFilter:(value:string)=>{}, dateRange:[], setDateRange:([])=>void}} props
 * @returns
 */
export const FilterSelector = props => {
  const [lang] = useLang();
  const { filterType, handleFilter, dateRange, setDateRange } = props;
  const [isCustomDate, setisCustomDate] = useState(false);
  return isCustomDate ? (
    <div className="flex-row-centered gap-1">
      <DatePicker.RangePicker
        onChange={values => {
          if (values) {
            const startDate = values[0] ? dayjs(values[0]).startOf("day") : null;
            const endDate = values[1] ? dayjs(values[1]).endOf("day") : null;
            setDateRange([startDate?.toISOString(), endDate?.toISOString()]);
          } else {
            setDateRange([]);
          }
        }}
      />
      <LuCircleX
        size={16}
        strokeWidth={1.75}
        className="cursor-pointer"
        onClick={() => {
          setisCustomDate(false);
          setDateRange([]);
        }}
      />
    </div>
  ) : (
    <Select
      value={filterType}
      className="w-[200px]"
      options={[
        {
          value: "TODAY",
          label: "Today",
        },
        {
          value: "YESTERDAY",
          label: "Yesterday",
        },
        {
          value: THIS_MONTH,
          label: "Month to date",
        },
        {
          value: LAST_MONTH,
          label: "Last Month",
        },
        {
          value: THIS_WEEK,
          label: "This Week",
        },
        {
          value: LAST_WEEK,
          label: "Last Week",
        },
        {
          value: LAST_30_DAYS,
          label: "Last 30 Days",
        },
      ]}
      dropdownRender={menu => {
        return (
          <>
            {menu}
            <span onClick={() => setisCustomDate(true)} className="cursor-pointer p-2 pl-3 ">
              Custom date
            </span>
          </>
        );
      }}
      onChange={handleFilter}
    />
  );
};

// Map filter value to a human-readable period label used in tooltips/text
const getFilterPeriodLabel = (filterType, dateRange) => {
  try {
    if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
      // dateRange expected as ISO strings
      const start = dayjs(dateRange[0]);
      const end = dayjs(dateRange[1]);
      if (start.isValid() && end.isValid()) {
        return `${start.format(DATE_TIME_FORMATS.MMM_DD_YYYY)} - ${end.format(
          DATE_TIME_FORMATS.MMM_DD_YYYY
        )}`;
      }
    }
  } catch (e) {
    // noop
  }
  const map = {
    TODAY: "today",
    YESTERDAY: "yesterday",
    THIS_MONTH: "this month",
    LAST_MONTH: "last month",
    THIS_WEEK: "this week",
    LAST_WEEK: "last week",
    LAST_30_DAYS: "the last 30 days",
  };
  return map[filterType] || "the selected period";
};

/**
 * Inline filter-period label that mirrors what's selected in the header date
 * filter. Preset (Today / Month to date / ...): shows the preset label with a
 * hover tooltip revealing the resolved date range. Custom: renders the range
 * directly since there is no preset label to hover.
 */
const FilterPeriodLabel = ({ filterType, dateRange, className = "" }) => {
  const isCustom = filterType === CUSTOM;
  const range = formatFilterDateRangeOnly(filterType, dateRange);
  const label = formatFilterLabelOnly(filterType);
  const baseClass = `text-sm text-grey-500 ${className}`.trim();

  if (isCustom) {
    if (!range) return null;
    return <span className={baseClass}>{range}</span>;
  }
  if (!label) return null;
  return (
    <Tooltip title={range || ""} mouseEnterDelay={0.2}>
      <span className={`${baseClass} cursor-help`}>{label}</span>
    </Tooltip>
  );
};
/**
 *
 * @param {{total:number, pageSize:number, page:number,length:number, onChange:(page:number,pageSize:number)=>void,pageSizeOptions:number[]}} props
 * @returns
 */
/**
 * Generic per-column sort state for dashboard tables where any of several
 * columns can be the sort key (e.g. Message Type: User / Texts / Images /
 * Videos / Docs / Audio, and Performance by Sources tables).
 *
 * - The `key` on each AntD column definition is used as the `orderBy` value
 *   sent to the backend, so the column key MUST be one of the backend-
 *   supported values (see `ORDER_BY_FIELDS`).
 * - When no sort is active, `sortVariables` is empty so the backend picks its
 *   own default (e.g. `best_user_stats` for getUserMessageTypeAnalysis).
 *
 * Usage:
 *   const { sortVariables, getSortOrder, onSortChange } = useColumnSort();
 *   // on each sortable column:
 *   //   key: ORDER_BY_FIELDS.TEXT_MESSAGES,
 *   //   sorter: true,
 *   //   sortOrder: getSortOrder(ORDER_BY_FIELDS.TEXT_MESSAGES),
 *   // on the Table:
 *   //   onChange={(_, __, sorter) => onSortChange(sorter)}
 */
const useColumnSort = () => {
  const [sortState, setSortState] = useState({ orderBy: null, orderDirection: null });

  const onSortChange = sorter => {
    if (sorter?.order === SORT_DIRECTION.ASCEND) {
      setSortState({
        orderBy: sorter?.columnKey || sorter?.column?.key || null,
        orderDirection: SORT_DIRECTION.ASC,
      });
    } else if (sorter?.order === SORT_DIRECTION.DESCEND) {
      setSortState({
        orderBy: sorter?.columnKey || sorter?.column?.key || null,
        orderDirection: SORT_DIRECTION.DESC,
      });
    } else {
      setSortState({ orderBy: null, orderDirection: null });
    }
  };

  const getSortOrder = key => {
    if (!sortState.orderDirection || sortState.orderBy !== key) return null;
    return sortState.orderDirection === SORT_DIRECTION.ASC
      ? SORT_DIRECTION.ASCEND
      : SORT_DIRECTION.DESCEND;
  };

  const sortVariables =
    sortState.orderBy && sortState.orderDirection
      ? { orderBy: sortState.orderBy, orderDirection: sortState.orderDirection }
      : {};

  return { sortVariables, sortState, getSortOrder, onSortChange };
};

// Shared pagination config for AntD Table built-in pagination (left aligned, consistent across dashboard).
// Optional `override` slots in a preset config (e.g. `COMMON_PAGINATION`) for
// callers that want the app-wide pagination shape; unset for the dashboard
// so its long-standing config (including the "6" size) stays untouched.
const buildTablePagination = ({ total, page, pageSize, setParamsFn, override }) => ({
  total: total || 0,
  current: page,
  pageSize,
  size: "small",
  position: override?.TABLE_POSITION || ["bottomLeft"],
  showSizeChanger: true,
  pageSizeOptions: override?.PAGE_SIZE_OPTIONS || [6, 10, 20, 50, 100],
  showTotal: (t, range) => `Showing ${range[1] - range[0] + 1} out of ${t}`,
  onChange: (p, ps) => setParamsFn(prev => ({ page: ps !== prev.pageSize ? 1 : p, pageSize: ps })),
});

export const AnalyticsPagination = props => {
  return (
    <div className="flex-row-centered flex-wrap gap-3 justify-start">
      <Pagination
        showSizeChanger
        size="small"
        showTotal={(total, range) => `Showing ${range[1] - range[0] + 1} out of ${total}`}
        {...props}
        current={props.page}
      />
    </div>
  );
};
/**
 * ![Messages Sent vs. Received](https://img001.prntscr.com/file/img001/oZu_JQUqQ_2uWnYvOU2lGQ.png)
 * ### [Figma Link](https://www.figma.com/design/NDVwkatBby4TFCN5oag0D1/1.-Web?node-id=12729-12531&t=ojvDzhXjpXYtiRcI-4)
 * @param {{
 * type: "NORMAL" | "GROUP" | "STACK",
 * xField?:string,
 * yField?:string,
 * seriesField?:string}} props
 * @returns
 */
export const UserAnalytics = props => {
  const { filterType, dateRange, userIds, apiUserIds, apiTeamIds, opportunityTypeId } =
    useDashboardFilter();
  const dispatch = useDispatch();
  const [analytics, setanalytics] = useState({ data: [], count: 0 });
  const [appointmentAnalytics, setappointmentAnalytics] = useState({ data: [] });
  const [params, setparams] = useState({
    page: 1,
    pageSize: 10,
  });
  useEffect(() => {
    setparams(prev => ({ ...prev, page: 1 }));
  }, [filterType, dateRange, userIds]);

  const userCondition = useMemo(() => userIds.length > 0 && userIds.length <= 3, [userIds]);
  const { refetch: getResponseRecivedByAttemp, loading: engagementAnayticLoader } = useQuery(
    GET_USER_MESSAGE_ANALYSIS,
    {
      fetchPolicy: "network-only",
      variables: {
        filterType,
        userIds: apiUserIds,
        teamIds: apiTeamIds,
        dateRange,
        opportunityTypeId,
        ...(userCondition ? {} : params),
      },
      onCompleted: res => {
        setanalytics(res.getUserMessageAnalysis);
      },
    }
  );

  // Reshape the flat API response into a grouped+stacked chart shape. The
  // chart shows three bar positions per user (Sent / Received / Engagement);
  // the Sent bar is itself stacked from three segments (Otto / AI-assisted /
  // Manual). Received and Engagement are single-segment bars.
  //
  // API emits six rows per user: `totalSent`, `ottoSent`, `aiAssistedSent`,
  // `manualSent`, `receivedCount`, `engagement`. We expand the Sent total
  // into three stacked rows and keep Received / Engagement as one row each
  // → five rows per user. Sidecar fields (`totalSent`, `received`,
  // `engagement`) ride on every row so the shared tooltip can read them
  // from `firstData` regardless of which segment the cursor lands on (see
  // `messageSentReceivedTooltip` in barChart.js).
  const chartData = useMemo(() => {
    const byUser = new Map();
    // Track which (user, field) pairs we've already filled. The API can emit
    // a user twice with different values (BE returns duplicate sections in
    // some responses); we trust the FIRST occurrence and drop subsequent
    // duplicates. Accumulating would double-count; overwriting would keep
    // the smaller "last" value instead of the authoritative first one.
    const seenFields = new Set();
    (analytics.data || []).forEach(item => {
      const user = item.userFullName;
      if (!byUser.has(user)) {
        byUser.set(user, {
          userFullName: user,
          totalSent: 0,
          ottoSent: 0,
          aiAssistedSent: 0,
          manualSent: 0,
          receivedCount: 0,
          engagement: 0,
        });
      }
      const bucket = byUser.get(user);
      const fieldKey = `${user}::${item.name}`;
      if (Object.prototype.hasOwnProperty.call(bucket, item.name) && !seenFields.has(fieldKey)) {
        bucket[item.name] = Number(item.value) || 0;
        seenFields.add(fieldKey);
      }
    });
    const out = [];
    byUser.forEach(b => {
      // Fallback for totalSent in case the BE row hasn't shipped yet — sum
      // the three segments so the tooltip still reads correctly.
      const totalSent = b.totalSent || b.ottoSent + b.aiAssistedSent + b.manualSent;
      // Full sidecar carrying every value the tooltip needs. G2's
      // group+stack mode doesn't reliably surface stacked-segment names in
      // the shared tooltip's `items[]`, so we read all numbers off the row
      // data instead of trying to parse item names.
      const sidecar = {
        userFullName: b.userFullName,
        totalSent,
        otto: b.ottoSent,
        aiAssisted: b.aiAssistedSent,
        manual: b.manualSent,
        received: b.receivedCount,
        engagement: b.engagement,
      };
      // Sent group — stacked from three series.
      out.push({ ...sidecar, group: "Sent", name: "Manual", value: b.manualSent });
      out.push({ ...sidecar, group: "Sent", name: "AI-assisted", value: b.aiAssistedSent });
      out.push({ ...sidecar, group: "Sent", name: "Otto", value: b.ottoSent });
      // Received and Engagement — single-segment bars.
      out.push({ ...sidecar, group: "Received", name: "Received", value: b.receivedCount });
      out.push({ ...sidecar, group: "Engagement", name: "Engagement", value: b.engagement });
    });
    return out;
  }, [analytics.data]);

  return (
    <div className="flex-col-centered gap-1">
      <MetricHeader title="Messages Sent vs. Received" metricDetails={MESSAGES_SENT_VS_RECEIVED}>
        <CustomButton
          type="link"
          btnText="View detailed report"
          handleClick={() => goToMessageDetail(MESSAGE_REPORT_SOURCE.SENT_VS_RECEIVED, dispatch)}
        />
      </MetricHeader>
      <div className="sm:flex-row-centered flex-col-centered gap-1">
        {/* Messages Sent vs. Received graph */}
        <GraphAnalysis
          graphKey="messageSentRecieved"
          label="Messages Sent vs. Received"
          data={chartData}
          width={8}
          API={getResponseRecivedByAttemp}
          filterType={filterType}
          loader={engagementAnayticLoader}
          userIds={userIds}
          type={props.type || "GROUP"}
          xField="userFullName"
          yField="value"
          seriesField="name"
          groupField="group"
          pagination={false}
          showFilters={false}
        />
        {/* Appointment analysis graph */}
        {/* {userCondition && (
          <AppointmentBySalesPersonGraph
            userIds={userIds}
            responseRecievedByAttempFilter={filterType}
            userCondition={userCondition}
            setappointmentAnalytics={setappointmentAnalytics}
            appointmentAnalytics={appointmentAnalytics}
            getResponseRecivedByAttemp={getResponseRecivedByAttemp}
            type={props.type || "GROUP"}
          />
        )} */}
      </div>
      {!userCondition && (
        <AnalyticsPagination
          total={analytics.count}
          pageSize={params?.pageSize || 0}
          length={chartData.length / 3}
          onChange={(page, pageSize) =>
            setparams(prev => ({ page: pageSize !== prev.pageSize ? 1 : page, pageSize }))
          }
          page={params.page}
        />
      )}
    </div>
  );
};
const AppointmentBySalesPersonGraph = ({
  userIds,
  responseRecievedByAttempFilter,
  userCondition,
  setappointmentAnalytics,
  appointmentAnalytics,
  getResponseRecivedByAttemp,
  type,
}) => {
  const { loading: appointmentLoader } = useQuery(GET_APPOINTMENTS_BY_SALES_PERSON, {
    variables: {
      userIds,
      filterType: responseRecievedByAttempFilter,
      fields: ["scheduled", "showed", "no_showed"],
      outputFormat: "distributed_object",
      orderDirection: "desc",
      orderBy: "showed",
      pageSize: 3,
    },
    onCompleted: res => {
      setappointmentAnalytics(
        res?.getAppointmentSalesperson?.distributedData?.map(item => ({
          ...item,
          userFullName: item.user.fullName,
          fieldName: capitalize(item.name.replace("_", " ")),
        }))
      );
    },
  });
  return (
    <GraphAnalysis
      graphKey="messageSentRecieved"
      label="Messages Sent vs. Received"
      data={appointmentAnalytics}
      width={8}
      colors={["#FACC15", "#FB923C", "#EF4444"]}
      API={getResponseRecivedByAttemp}
      filterType={responseRecievedByAttempFilter}
      loader={appointmentLoader}
      userIds={userIds}
      type={type || "GROUP"}
      xField="userFullName"
      yField="value"
      seriesField="fieldName"
      pagination={false}
      showFilters={false}
    />
  );
};
/**
 * ![Engagement Analysis](https://img001.prntscr.com/file/img001/td9rbCINS4S-nCWJl3sI6Q.png)
 * ### [Figma Link](https://www.figma.com/design/NDVwkatBby4TFCN5oag0D1/1.-Web?node-id=12729-13146&t=ojvDzhXjpXYtiRcI-4)
 * @returns
 */
export const EngagementAnalysis = () => {
  const { filterType, dateRange, userIds, apiUserIds, apiTeamIds, opportunityTypeId } =
    useDashboardFilter();
  const { data: resp, loading: engagementAnalyticsLoader } = useQuery(GET_ENGAGEMENT_ANALYTICS, {
    fetchPolicy: "network-only",
    variables: {
      filterType,
      dateRange,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      opportunityTypeId,
    },
  });
  const analyticsData = resp?.getEngagementAnalytics || [];
  const counts = useMemo(() => activityAnalysis(filterByRange(analyticsData)), [analyticsData]);

  const getCount = useCallback(
    title => {
      const item = counts.find(count => count.title === title);
      return item?.count || 0;
    },
    [counts]
  );
  const engagementData = useMemo(
    () => [
      {
        label: "Sent Messages",
        value: getCount("Total Messages Sent"),
        metricDetails: SENT_MESSAGES,
      },
      {
        label: "Received Messages",
        value: getCount("Total Messages") - getCount("Total Messages Sent"),
        metricDetails: RECEIVED_MESSAGES,
      },
    ],
    [getCount]
  );

  const renderDataItem = ({ label, value, isRed }) => (
    <div key={label} className="flex-col-centered">
      <span className="engagement-analysis-label">{label}</span>
      <Skeleton loading={engagementAnalyticsLoader} active paragraph={{ rows: 0 }}>
        <span className={`engagement-analysis-value ${isRed ? "text-red-500" : ""}`}>{value}</span>
      </Skeleton>
    </div>
  );

  return (
    <div className="flex-col-centered items-start">
      <MetricHeader title="Engagement Analysis" className="w-full mb-10" />
      <div className="flex-row-centered w-full">
        <EngagementVector />
        <div className="flex-col-centered gap-1 pl-2 w-[calc(100%-153px)]">
          {/* {[0].map(index => (
            <div
              key={index}
              className="border-dashed border flex-row-centered items-center justify-between border-grey-300 rounded-lg px-6 py-3 w-full"
            >
              {renderDataItem(engagementData[index * 2])}
              {renderDataItem(engagementData[index * 2 + 1])}
            </div>
          ))} */}
          <div className="flex-row-centered w-full gap-4">
            {engagementData.map((item, index) => {
              return (
                <div
                  key={index}
                  className="border-dashed w-1/2 rounded-lg px-6 py-3 border border-grey-300 flex-col-centered"
                >
                  <span className="text-xs text-[14px] font-[400] text-grey-500 flex-row-centered gap-1">
                    {item.label}
                    <SubMetricInfoIcon metricDetails={item.metricDetails} />
                  </span>
                  <Skeleton active paragraph={{ rows: 0 }} loading={engagementAnalyticsLoader}>
                    <span className="font-[600] text-lg text-grey-700">{item.value}</span>
                  </Skeleton>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Render preset filters as "<Label> (start - end)" (e.g. "Month to date (May 1, 2026 - May 18, 2026)")
// and custom as the bare range. Delegates to the shared util in utils/dateTimes.js.
const getDateFilterDisplayLabel = (filterType, dateRange) =>
  formatFilterDateRangeLabel(filterType, dateRange);

/**
 * ![Appointment analysis](https://img001.prntscr.com/file/img001/RuqhP-wFQXSAdgimm16WSw.png)
 * ### [Figma Link](https://www.figma.com/design/NDVwkatBby4TFCN5oag0D1/1.-Web?node-id=12729-13206&t=ojvDzhXjpXYtiRcI-4)
 * @returns
 */
export const AppointmentAnalysis = () => {
  const {
    filterType,
    dateRange,
    dateRangeDisplay,
    userIds,
    apiUserIds,
    apiTeamIds,
    opportunityTypeId,
  } = useDashboardFilter();
  const dispatch = useDispatch();
  const [dateBasis, setDateBasis] = useState(DATE_BASIS.CREATED);
  // "Digital Appointment Analysis" is Internet-source-scoped by definition.
  // The drill-ins from this metric must carry the Internet branch (parent +
  // every descendant) as the source filter.
  const { getIdsWithChildrenByName } = useOpportunitySources();
  const internetSourceIds = useMemo(
    () => getIdsWithChildrenByName("Internet"),
    [getIdsWithChildrenByName]
  );

  // Same opportunity-type expansion the sales-person / source drill-ins
  // use — Lead Center's opportunity query filters by leaf type ids, so a
  // parent-only id wouldn't return any children. Expand into
  // [parent + every descendant] before dispatching.
  const { expandWithDescendants } = useOpportunityTypeTree();
  const {
    data: resAppointments,
    refetch: getAppointmentAnalysis,
    loading: appointmentAnalysisLoader,
  } = useQuery(GET_APPOINTMENT_ANALYSIS, {
    fetchPolicy: "network-only",
    variables: {
      filterType,
      dateRange,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      dateType: dateBasis,
      opportunityTypeId,
    },
  });
  const data = useMemo(
    () => appointmentsAnalytics(filterByRange(resAppointments?.getAppointmentAnalysis.data)),
    [resAppointments?.getAppointmentAnalysis.data]
  );
  const growthRate = useMemo(() => {
    const scheduledAppointments = resAppointments?.getAppointmentAnalysis?.data?.find(
      item => item.title === "SCHEDULED"
    );
    if (scheduledAppointments) {
      return {
        trend: scheduledAppointments?.growthRate < 0 ? "DOWN" : "UP",
        percent: scheduledAppointments?.growthRate
          ? Math.abs(scheduledAppointments?.growthRate).toString()
          : 0,
      };
    }
    return {
      trend: "DOWN",
      percent: 0,
    };
  }, [resAppointments]);

  const total = useMemo(() => {
    return data.reduce((acc, curr) => acc + curr.count, 0);
  }, [data]);

  const appointmentBookPeriodLabel = useMemo(
    () => getFilterPeriodLabel(filterType, dateRangeDisplay),
    [filterType, dateRangeDisplay]
  );

  const count = data.reduce((total, item) => total + item.count, 0);
  const config = {
    data: data.map(item => ({
      ...item,
      title: capitalize(convertToTitleCase(item.title)).replace(/\s+/g, ""),
    })),
    angleField: "count",
    colorField: "title",
    radius: 0.8,
    innerRadius: 0.6,
    label: false,
    interactions: [{ type: "element-selected" }, { type: "element-active" }],
    statistic: {
      title: {
        style: { fontSize: 14 },
        formatter: () => "Total Appoint.",
      },
      content: {
        style: { fontSize: 24 },
        formatter: () => resAppointments?.getAppointmentAnalysis?.totalAppointments || 0,
      },
    },

    legend: {
      itemName: {
        formatter: (text, item) => {
          // The chart's title field has spaces stripped (needed so it can be
          // used as a stable colorField key), which makes statuses like
          // "No Showed" render as the single word "Noshowed". Map the
          // problematic ones back to a human label for the legend only —
          // the underlying title key is untouched so chart slices/colors
          // still line up.
          const STATUS_LABEL_OVERRIDES = { Noshowed: "No show" };
          const displayText = STATUS_LABEL_OVERRIDES[text] || text;
          const dataItem = data.find(
            d => capitalize(convertToTitleCase(d.title)).replace(/\s+/g, "") === item.value
          );
          if (dataItem) {
            // Guard against count === 0 → 0/0 = NaN. With no data, show 0%.
            const pct = count > 0 ? Math.floor((dataItem.count / count) * 100) : 0;
            return `${displayText}: ${dataItem.count} (${pct}%)`;
          }
          return displayText;
        },
      },
    },
    // Add title configuration at the root level
    title: {
      visible: true,
      text: "My Custom Pie Chart Title",
      style: {
        fontSize: 16,
        fontWeight: "bold",
        textAlign: "center",
      },
    },
  };

  const newLeadCount = resAppointments?.getAppointmentAnalysis?.totalNewLeads || 0;
  const bookedAppointmentCount =
    resAppointments?.getAppointmentAnalysis?.totalBookedAppointments || 0;

  // Drill into Lead Center → Opportunity view, scoped to the Internet source
  // branch and "Updated on" date basis (mirrors how the New Lead metric is
  // computed for this Internet-only tile). Other context (date range,
  // user/team) flows from the dashboard.
  const goToNewLeadOpportunities = () => {
    const dr =
      Array.isArray(dateRangeDisplay) && dateRangeDisplay.length === 2
        ? dateRangeDisplay
        : dateRange;
    dispatch(
      setOpportunityFilters({
        entityMode: apiTeamIds && apiTeamIds.length > 0 ? "teams" : "users",
        activeFilterMode: apiTeamIds && apiTeamIds.length > 0 ? "teams" : "users",
        teamIds: apiTeamIds || [],
        assignedUserIds: apiUserIds || [],
        userTypesOr: [],
        userTypesAnd: [],
        opportunitySource: (internetSourceIds || []).map(String),
        opportunityStatus: [],
        opportunityType:
          opportunityTypeId != null
            ? expandWithDescendants(
                Array.isArray(opportunityTypeId) ? opportunityTypeId[0] : opportunityTypeId
              ).map(String)
            : [],
        filterType,
        dateRange: dr,
        // Mirror the tile's basis toggle into the opportunity enum:
        // CREATED → Created on, DUE → Updated on (matches goToOpportunities).
        dateType:
          dateBasis === DATE_BASIS.CREATED
            ? OPPORTUNITY_DATE_BASIS.CREATED
            : OPPORTUNITY_DATE_BASIS.UPDATED,
        // Tile is current-status, not waterfall — pin NORMAL so the drill-in
        // list reproduces the tile's number.
        viewMode: VIEW_MODES.NORMAL,
        cameFromDashboard: true,
      })
    );
    dispatch(setForceOpportunityView(true));
    navigate(ROUTES.engagementsLeadCenter);
  };

  // Drill into Appointments page, scoped to the Internet source branch.
  // Carries the dashboard's user/team scope, opportunity type and date window.
  const goToBookedAppointments = () => {
    const isCustom = filterType === CUSTOM;
    const dr =
      Array.isArray(dateRangeDisplay) && dateRangeDisplay.length === 2
        ? dateRangeDisplay
        : dateRange;
    dispatch(
      reducerSetAppointmentFilters({
        entityMode: apiTeamIds && apiTeamIds.length > 0 ? "teams" : "users",
        activeFilterMode: apiTeamIds && apiTeamIds.length > 0 ? "teams" : "users",
        userIds: apiUserIds || [],
        teamIds: apiTeamIds || [],
        userMode: 1,
        appointmentStatus: [],
        opportunitySourceIds: internetSourceIds || [],
        cameFromDashboard: true,
        opportunityTypeId: opportunityTypeId ?? null,
        // Same rationale as the New Lead drill-in: tile is current-status,
        // not waterfall — pin viewMode so the appointment list matches.
        viewMode: VIEW_MODES.NORMAL,
        // Mirror the tile's basis toggle (same mapping as goToAppointments):
        // CREATED → CREATED_ON, DUE → DUE_ON.
        dateMode:
          dateBasis === DATE_BASIS.CREATED
            ? APPOINTMENT_DATE_MODE.CREATED_ON
            : APPOINTMENT_DATE_MODE.DUE_ON,
        dateType: isCustom ? APPOINTMENT_DATE_CUSTOM : filterType,
        customRange: isCustom
          ? {
              startDate: dayjs(dr?.[0]).format(DATE_TIME_FORMATS.YYYY_MM_DD),
              endDate: dayjs(dr?.[1]).format(DATE_TIME_FORMATS.YYYY_MM_DD),
            }
          : { startDate: null, endDate: null },
        page: 1,
        pageSize: 10,
      })
    );
    navigate(ROUTES.engagementsAppointment);
  };

  return (
    <>
      <MetricHeader title="Digital Appointment Analysis" metricDetails={APPOINTMENT_ANALYSIS_INFO}>
        <DateBasisDropdown value={dateBasis} onChange={setDateBasis} />
        <FilterPeriodLabel filterType={filterType} dateRange={dateRangeDisplay} />
      </MetricHeader>
      <div className="flex justify-center my-1">
        <div className="flex border border-dashed border-gray-300">
          <div className="flex flex-col items-center gap-1 p-3">
            <span className="text-sm text-gray-500 font-normal flex items-center gap-1">
              New Lead <SubMetricInfoIcon metricDetails={NEW_LEAD_APPOINTMENT} />
            </span>
            <Skeleton loading={appointmentAnalysisLoader} active paragraph={{ rows: 0 }}>
              {newLeadCount > 0 ? (
                <CustomButton
                  type="link"
                  className="!p-0 !h-auto !text-base !font-[600]"
                  btnText={newLeadCount}
                  handleClick={goToNewLeadOpportunities}
                />
              ) : (
                <span className="text-base font-[600] text-grey-700">{newLeadCount}</span>
              )}
            </Skeleton>
          </div>
          <div className="w-px border-gray-300 border border-dashed" />
          <div className="flex flex-col items-center gap-1 p-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 font-normal flex items-center gap-1">
                Booked Appoint.
                <SubMetricInfoIcon metricDetails={BOOKED_APPOINTMENTS} />
              </span>
            </div>
            <Skeleton loading={appointmentAnalysisLoader} active paragraph={{ rows: 0 }}>
              {bookedAppointmentCount > 0 ? (
                <CustomButton
                  type="link"
                  className="!p-0 !h-auto !text-base !font-[600]"
                  btnText={bookedAppointmentCount}
                  handleClick={goToBookedAppointments}
                />
              ) : (
                <span className="text-base font-[600] text-grey-700">{bookedAppointmentCount}</span>
              )}
            </Skeleton>
          </div>
          <div className="w-px border-gray-300 border border-dashed" />
          <div className="flex flex-col items-center gap-1 p-3">
            <span className="text-sm text-gray-500 font-normal flex items-center gap-1">
              % of Booked Appoint.
              <SubMetricInfoIcon metricDetails={BOOKED_APPOINTMENT_RATE} />
            </span>
            <Skeleton loading={appointmentAnalysisLoader} active paragraph={{ rows: 0 }}>
              <span className="text-base font-[600] text-grey-700">
                {resAppointments?.getAppointmentAnalysis?.appointmentBookingRate || 0}
              </span>
            </Skeleton>
          </div>
        </div>
      </div>
      <Skeleton loading={appointmentAnalysisLoader} className="h-[250px]" avatar active>
        <Pie className="h-[250px]" {...config} />
      </Skeleton>
      <Alert
        type={growthRate.trend === "UP" ? "success" : "error"}
        message={
          <span
            className={`${
              growthRate.trend === "UP" ? "text-green-500" : "text-red-500"
            } text-xs font-[400] flex-row-centered justify-center`}
          >
            Total appointments for {getDateFilterDisplayLabel(filterType, dateRangeDisplay)}{" "}
            {growthRate.trend === "UP" ? "increased" : "decreased"} by {growthRate.percent}%
            <DashboardArrow
              color={growthRate.trend === "UP" && "#4ae081"}
              className={`${growthRate.trend === "UP" && "rotate-180"}`}
            />
          </span>
        }
      />
    </>
  );
};

/**
 * ![Appointments by sales person](https://img001.prntscr.com/file/img001/nYfab-K_Qge_HPSC18MBUg.png)
 * ### [Figma Link](https://www.figma.com/design/NDVwkatBby4TFCN5oag0D1/1.-Web?node-id=12729-12836&t=ojvDzhXjpXYtiRcI-4)
 * @returns
 */
export const AppointmentBySalesPerson = ({
  // When provided, locks the view-type switcher (Appointment / Opportunity
  // / Communication / Activities) to this key — the dropdown becomes a
  // plain label. Used by CommunicationDetailReport to embed the
  // Communication view without the switcher.
  lockedViewType = null,
  // Hides the "View in fullscreen" button on the Communication tile —
  // set when this component is itself rendered inside the fullscreen
  // detail screen (so we don't offer a link to the page you're on).
  hideFullscreenButton = false,
  // Suppresses the entire MetricHeader (title + controls). The detail
  // screen provides its own frame + filters and doesn't want the tile
  // header repeated inside each tab.
  hideMetricHeader = false,
  // Switches the table's pagination options to the app-wide
  // `COMMON_PAGINATION` shape (10/20/50/100 + bottomLeft). Dashboard
  // callers leave this false to preserve the existing 6/10/20/50/100
  // config; the detail screen sets it to unify with the rest of the app.
  useCommonPagination = false,
  // Column-visibility hooks used by the detail screen. Parent lifts the
  // open state so it can drive a single "Columns" trigger in its filter
  // bar; the tile owns which columns are visible.
  columnPanelOpen = false,
  onCloseColumnPanel = () => {},
} = {}) => {
  const {
    filterType,
    dateRange,
    dateRangeDisplay,
    userIds,
    apiUserIds,
    apiTeamIds,
    opportunityTypeId,
  } = useDashboardFilter();
  const dispatch = useDispatch();

  // Opportunity types are needed at drill-in time to expand a picked parent
  // (e.g. "Service") into its full descendant ID list — the Lead Center
  // opportunity query filters by leaf type IDs, so sending only the parent
  // would otherwise return zero rows for parent-only selections.
  // `cache-first` keeps this a no-op when the dashboard's own cascader has
  // already populated the cache.
  const { expandWithDescendants } = useOpportunityTypeTree();

  const collectTypeIdsDeep = rootId => expandWithDescendants(rootId);

  const locationId = sessionStorage.getItem("currentLocationId");
  const [viewType, setViewType] = useState(lockedViewType || VIEW_TYPES.APPOINTMENT);
  const [viewMode, setViewMode] = useState(VIEW_MODES.WATERFALL);
  const [dateBasis, setDateBasis] = useState(DATE_BASIS.CREATED);
  const [opportunityDateBasis, setOpportunityDateBasis] = useState(OPPORTUNITY_DATE_BASIS.UPDATED);
  // Shared across Appointment / Opportunity / Messages views — each view has a
  // different set of sortable columns on the backend, so we reset when the
  // user switches views (see Dropdown onClick below).
  const { sortVariables, getSortOrder, onSortChange } = useColumnSort();
  // At most one column's filter can be active at a time:
  // { parentStatusId, selectedChildIds } | null
  const [activeColumnFilter, setActiveColumnFilter] = useState(null);

  // Child IDs for the single active column — sent to the query as `columnStatusIds`.
  const columnStatusIds = useMemo(
    () => activeColumnFilter?.selectedChildIds || [],
    [activeColumnFilter]
  );
  const [appointmentData, setAppointmentData] = useState({
    data: [],
    count: 0,
    totals: {},
  });
  const [opportunityData, setOpportunityData] = useState({
    totalCount: 0,
    statusTotals: [],
    data: [],
  });
  const [messagesData, setMessagesData] = useState({
    data: [],
    count: 0,
    totals: {},
  });
  const [activitiesData, setActivitiesData] = useState({
    data: [],
    count: 0,
    totals: {},
  });
  const [params, setparams] = useState({
    page: 1,
    // Dashboard callers keep the historical 6-row default; detail-screen
    // callers request 10 via `useCommonPagination` so it lines up with
    // COMMON_PAGINATION.DEFAULT_PAGE_SIZE.
    pageSize: useCommonPagination ? COMMON_PAGINATION.DEFAULT_PAGE_SIZE : 6,
  });

  // Opportunity view column visibility — defaults to all columns visible; the
  // user can hide any of them via the Show/Hide Columns drawer. Status columns
  // are dynamic (come from API statusTotals), so any newly-arrived keys are
  // added to the visible set automatically (see effect below).
  const [isOppColumnDrawerVisible, setIsOppColumnDrawerVisible] = useState(false);
  const [oppVisibleColumnKeys, setOppVisibleColumnKeys] = useState([]);
  const [oppTempVisibleColumnKeys, setOppTempVisibleColumnKeys] = useState([]);
  // Tracks the previous set of default column keys so the sync effect can
  // distinguish "key user just hid" from "key the API just introduced".
  // Without this, the effect re-adds any key the user removes (since
  // `oppDefaultColumnKeys` is rebuilt every render).
  const prevOppDefaultsRef = useRef([]);

  const locationIds = useMemo(() => {
    if (!locationId) return [];
    return String(locationId)
      .split(",")
      .filter(Boolean)
      .map(id => +id);
  }, [locationId]);

  useEffect(() => {
    setparams(prev => ({ ...prev, page: 1 }));
  }, [filterType, dateRange, userIds, columnStatusIds, viewMode]);

  const { loading: salesPersonLoader } = useQuery(GET_APPOINTMENTS_BY_SALES_PERSON, {
    fetchPolicy: "network-only",
    skip: viewType !== VIEW_TYPES.APPOINTMENT,
    variables: {
      filterType,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      dateRange,
      dateType: dateBasis,
      viewMode,
      opportunityTypeId,
      ...sortVariables,
      ...params,
    },
    onCompleted: res => {
      setAppointmentData(res.getAppointmentSalesperson);
    },
  });

  const { loading: opportunityLoader } = useQuery(GET_OPPORTUNITY_BY_SALES_PERSON, {
    fetchPolicy: "network-only",
    skip: viewType !== VIEW_TYPES.OPPORTUNITY,
    variables: {
      locationIds,
      filterType,
      dateRange,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      columnStatusIds,
      viewMode,
      dateType: opportunityDateBasis,
      opportunityTypeId,
      ...sortVariables,
      ...params,
    },
    onCompleted: res => {
      setOpportunityData(res.getOpportunityBySalesPerson);
    },
  });

  const { loading: messagesLoader } = useQuery(GET_MESSAGES_BY_SALES_PERSON, {
    fetchPolicy: "network-only",
    skip: viewType !== VIEW_TYPES.MESSAGES,
    variables: {
      filterType,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      dateRange,
      opportunityTypeId,
      ...sortVariables,
      ...params,
    },
    onCompleted: res => {
      setMessagesData(res.getMessagesBySalesperson || { data: [], count: 0, totals: {} });
    },
  });

  const { loading: activitiesLoader } = useQuery(GET_ACTIVITIES_BY_SALES_PERSON, {
    fetchPolicy: "network-only",
    skip: viewType !== VIEW_TYPES.ACTIVITIES,
    variables: {
      filterType,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      dateRange,
      opportunityTypeId,
      ...sortVariables,
      ...params,
    },
    onCompleted: res => {
      setActivitiesData(res.getActivitiesBySalesperson || { data: [], count: 0, totals: {} });
    },
  });

  const { data: statusTreeRes } = useQuery(GET_OPPORTUNITY_STATUS_NEW, {
    fetchPolicy: "network-only",
    variables: { locationIds },
    skip: viewType !== VIEW_TYPES.OPPORTUNITY,
  });

  // Build a lookup: top-level status key -> direct childs[].
  // Keys (statusIds) are unique so ID-based matching is reliable; only top-level
  // nodes are mapped because statusTotals columns represent top-level statuses.
  const statusChildrenMap = useMemo(() => {
    const map = {};
    (statusTreeRes?.getOpportunityStatusNew || []).forEach(n => {
      if (n.childs?.length) map[n.key] = n.childs;
    });
    return map;
  }, [statusTreeRes]);

  // Recursively map API statusCounts shape to ExpandableStatusList shape
  const mapStatusesForDrawer = items =>
    (items || []).map(s => ({
      status: s.statusName,
      count: s.totalCount,
      ...(s.children?.length ? { children: mapStatusesForDrawer(s.children) } : {}),
    }));

  // --- Appointment columns ---
  // `key` MUST match the backend `orderBy` value (snake_case) so AntD's
  // sorter.columnKey is sent verbatim. `dataIndex` is the camelCase response
  // field for cell rendering.
  const APPOINTMENT_STATUS_COLS = [
    {
      key: ORDER_BY_FIELDS.TOTAL_APPOINTMENTS,
      dataIndex: "totalAppointments",
      title: "Total Appointments",
      short: "Total Appts.",
    },
    {
      key: ORDER_BY_FIELDS.SCHEDULED,
      dataIndex: "scheduled",
      title: "Scheduled",
      apptStatus: "SCHEDULED",
    },
    {
      key: ORDER_BY_FIELDS.CONFIRMED,
      dataIndex: "confirmed",
      title: "Confirmed",
      apptStatus: "CONFIRMED",
    },
    { key: ORDER_BY_FIELDS.SHOWED, dataIndex: "showed", title: "Showed", apptStatus: "SHOWED" },
    { key: ORDER_BY_FIELDS.SOLD, dataIndex: "sold", title: "Sold" },
    {
      key: ORDER_BY_FIELDS.NO_SHOWED,
      dataIndex: "noShowed",
      title: "No-Showed",
      apptStatus: "NO_SHOWED",
    },
    {
      key: ORDER_BY_FIELDS.RESCHEDULED,
      dataIndex: "rescheduled",
      title: "Re-Scheduled",
      apptStatus: "RESCHEDULED",
    },
    {
      key: ORDER_BY_FIELDS.CANCELLED,
      dataIndex: "cancelled",
      title: "Cancelled",
      apptStatus: "CANCELLED",
    },
  ];

  // Build drawer statuses from a single appointment row (exclude the
  // "Total Appointments" aggregate column — drawer shows per-status counts only).
  const appointmentStatusesForDrawer = record =>
    APPOINTMENT_STATUS_COLS.filter(c => c.dataIndex !== "totalAppointments").map(c => ({
      status: c.title,
      count: record[c.dataIndex] || 0,
    }));

  // Click-through from a status count cell → Appointments page, pre-filtered to
  // that sales person + status, carrying the dashboard's opportunity type and
  // date context. Dashboard date presets (Today / Last Week / ...) map 1:1 to
  // the Appointments date-dropdown values, so we pass the preset through — the
  // dropdown then shows the matching label and the BE computes the same range.
  // Only a dashboard custom range falls back to the explicit CUSTOM range.
  const goToAppointments = (record, apptStatus, { allStatuses = false } = {}) => {
    // `record === null` means the summary-row click — no per-user scope,
    // carry the dashboard's committed user/team scope through instead.
    const isRowScoped = !!record;
    const rowUserId = isRowScoped ? +record?.user?.id : null;
    const statuses = allStatuses ? APPOINTMENT_STATUSES : [apptStatus].filter(Boolean);
    const teamsMode = !isRowScoped && Array.isArray(apiTeamIds) && apiTeamIds.length > 0;

    // Build the entire dashboard → appointment payload, then apply it in a
    // single dispatch so the appointment page sees one coherent filter update
    // instead of 8 intermediate states. Keys mirror the slice fields exactly.
    const isCustom = filterType === CUSTOM;
    const dr =
      Array.isArray(dateRangeDisplay) && dateRangeDisplay.length === 2
        ? dateRangeDisplay
        : dateRange;
    dispatch(
      reducerSetAppointmentFilters({
        // Per-row drill is always user-scoped (the row IS a user). Summary
        // drill carries whatever mode the dashboard committed to.
        entityMode: isRowScoped ? "users" : teamsMode ? "teams" : "users",
        activeFilterMode: isRowScoped ? "users" : teamsMode ? "teams" : "users",
        teamIds: isRowScoped ? [] : teamsMode ? apiTeamIds : [],
        userIds: isRowScoped ? (rowUserId ? [rowUserId] : []) : apiUserIds || [],
        // "By Sales Person" attributes the appointment to its owner → Assigned To.
        userMode: 1,
        appointmentStatus: statuses,
        opportunityTypeId: opportunityTypeId ?? null,
        // Carry the waterfall / current-status toggle so the page reproduces
        // the same count the user just clicked.
        viewMode,
        // Sales-Person drill-in is user-scoped, not source-scoped — clear any
        // leftover source filter from a prior "by source" navigation.
        opportunitySourceIds: [],
        cameFromDashboard: true,
        // Metric basis (created_date / due_date) → page's filterDateType enum.
        dateMode:
          dateBasis === DATE_BASIS.CREATED
            ? APPOINTMENT_DATE_MODE.CREATED_ON
            : APPOINTMENT_DATE_MODE.DUE_ON,
        // CUSTOM → explicit start/end window; presets pass through unchanged.
        dateType: isCustom ? APPOINTMENT_DATE_CUSTOM : filterType,
        customRange: isCustom
          ? {
              startDate: dayjs(dr?.[0]).format(DATE_TIME_FORMATS.YYYY_MM_DD),
              endDate: dayjs(dr?.[1]).format(DATE_TIME_FORMATS.YYYY_MM_DD),
            }
          : { startDate: null, endDate: null },
        page: 1,
        pageSize: 10,
      })
    );
    navigate(ROUTES.engagementsAppointment);
  };

  // Click-through from an opportunity status-count cell → Lead Center's
  // Opportunity view, pre-filtered to that sales person + status, carrying the
  // dashboard's opportunity type, view mode, and date context. Mirrors
  // `goToAppointments` but targets the opportunity slice + lead-center route.
  // The dashboard's parent-status count includes its child statuses, so we send
  // the parent statusId plus every child id to keep the list total in sync.
  const goToOpportunities = (record, st) => {
    const userId = +record?.userId;
    const childIds = (statusChildrenMap[st.statusId] || []).map(c => +c.key).filter(Boolean);
    const statusIds = [+st.statusId, ...childIds].filter(Boolean);
    // Dashboard date window is already in the API-ready shape; the display
    // sidecar wins when present (matches goToAppointments).
    const dr =
      Array.isArray(dateRangeDisplay) && dateRangeDisplay.length === 2
        ? dateRangeDisplay
        : dateRange;
    dispatch(
      setOpportunityFilters({
        // Sales-Person drill is always user-scoped (the row IS a user), so
        // force users mode and clear any team/CRM-owner-type leftovers.
        entityMode: "users",
        activeFilterMode: "users",
        teamIds: [],
        assignedUserIds: userId ? [userId] : [],
        userTypesOr: [],
        userTypesAnd: [],
        opportunityStatus: statusIds,
        // User-scoped drill, not source-scoped — clear any leftover source.
        opportunitySource: [],
        // Lead Center's opportunity query filters by exact type IDs — a
        // parent ID alone won't match any child-type leads. Expand the
        // dashboard's picked type into [parent + every descendant] so a
        // root pick like "Service" pulls leads under all its sub-types too.
        // Lead Center's filter tree is also keyed by these real IDs (see
        // its GET_OPPORTUNITTIES_TYPES onCompleted), so the same expanded
        // list pre-checks the column filter visually. Strings because AntD
        // `checkedKeys` compares as strings.
        opportunityType:
          opportunityTypeId != null
            ? collectTypeIdsDeep(
                Array.isArray(opportunityTypeId) ? opportunityTypeId[0] : opportunityTypeId
              ).map(String)
            : [],
        // Carry the waterfall / current-status toggle and date context so the
        // list reproduces the same count the user clicked.
        viewMode,
        filterType,
        dateRange: dr,
        dateType: opportunityDateBasis,
        cameFromDashboard: true,
      })
    );
    dispatch(setForceOpportunityView(true));
    navigate(ROUTES.engagementsLeadCenter);
  };

  const appointmentColumns = [
    // {
    //   title: "",
    //   key: "expand",
    //   width: 40,
    //   fixed: "left",
    //   render: (_, record) => (
    //     <Image
    //       src={CollapseIcon}
    //       alt="Expand"
    //       width={16}
    //       height={16}
    //       preview={false}
    //       className="cursor-pointer"
    //       onClick={() =>
    //         openDrawer(
    //           DRAWER_TYPES.OPPORTUNITY_SALES_DETAIL,
    //           {
    //             title: (
    //               <div className="flex flex-col">
    //                 <span className="text-base font-semibold">{record?.user?.fullName || ""}</span>
    //                 <span className="text-xs font-normal text-gray-500">
    //                   {record?.user?.companyRoleName || ""}
    //                 </span>
    //               </div>
    //             ),
    //             width: 400,
    //           },
    //           {
    //             statuses: appointmentStatusesForDrawer(record),
    //             total: record.totalOpportunities || 0,
    //           }
    //         )
    //       }
    //     />
    //   ),
    // },
    {
      title: "User",
      key: ORDER_BY_FIELDS.FULL_NAME,
      width: 200,
      fixed: "left",
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.FULL_NAME),
      render: (_, record) => (
        <span className="flex-row-centered gap-1">
          <CustomAvatar
            name={record?.user?.fullName}
            src={record?.user?.profilePic}
            bgClass="bg-grey-700"
          />
          <Tooltip title={record?.user?.fullName || ""}>
            <CheckoutReportUserLink
              userId={record?.user?.id}
              name={record?.user?.fullName || ""}
              className="line-clamp-2 break-words leading-tight max-w-[120px]"
            />
          </Tooltip>
        </span>
      ),
    },
    ...APPOINTMENT_STATUS_COLS.map(col => ({
      title: (
        <span className="block text-center leading-tight break-words whitespace-normal">
          {col.short || col.title}
        </span>
      ),
      dataIndex: col.dataIndex,
      key: col.key,
      width: 100,
      align: "center",
      sorter: true,
      sortOrder: getSortOrder(col.key),
      // Status counts click through to that user + status. The
      // "Total Appointments" column drills in with ALL statuses selected
      // (roll-up drill). Zero counts stay plain text.
      render: (val, record) => {
        const count = val || 0;
        if (col.apptStatus && count > 0) {
          return (
            <CustomButton
              type="link"
              className="!p-0 !h-auto"
              btnText={count}
              handleClick={() => goToAppointments(record, col.apptStatus)}
            />
          );
        }
        if (col.dataIndex === "totalAppointments" && count > 0) {
          return (
            <CustomButton
              type="link"
              className="!p-0 !h-auto"
              btnText={count}
              handleClick={() => goToAppointments(record, null, { allStatuses: true })}
            />
          );
        }
        return count;
      },
    })),
  ];

  // --- Opportunity columns (dynamic from API statusTotals) ---
  const opportunityColumns = [
    {
      title: "",
      key: "expand",
      width: 40,
      fixed: "left",
      render: (_, record) => (
        <Image
          src={CollapseIcon}
          alt="Expand"
          width={16}
          height={16}
          preview={false}
          className="cursor-pointer"
          onClick={() =>
            openDrawer(
              DRAWER_TYPES.OPPORTUNITY_SALES_DETAIL,
              {
                title: (
                  <div className="flex flex-col">
                    <span className="text-base font-semibold">{record.userName || ""}</span>
                    <span className="text-xs font-normal text-gray-500">
                      {record.companyRoleName || ""}
                    </span>
                  </div>
                ),
                width: 400,
              },
              {
                statuses: mapStatusesForDrawer(record.statusCounts),
                total: record.totalCount || 0,
              }
            )
          }
        />
      ),
    },
    {
      title: "User",
      key: ORDER_BY_FIELDS.FULL_NAME,
      fixed: "left",
      width: 100,
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.FULL_NAME),
      render: (_, record) => (
        <span className="flex-row-centered gap-1">
          <CustomAvatar
            name={record?.userName || ""}
            src={record?.userProfileImage}
            bgClass="bg-grey-700"
          />
          <Tooltip title={record?.userName || ""}>
            <CheckoutReportUserLink
              userId={record?.userId}
              name={record?.userName || ""}
              className="line-clamp-2 break-words leading-tight max-w-[120px]"
            />
          </Tooltip>
        </span>
      ),
    },
    // {
    //   title: (
    //     <Tooltip title="Total Opportunities">
    //       <span className="block text-center leading-tight break-words whitespace-normal">
    //         Total Opportunities
    //       </span>
    //     </Tooltip>
    //   ),
    //   key: "totalOpportunities",
    //   dataIndex: "totalOpportunities",
    //   align: "center",
    //   width: 140,
    //   onHeaderCell: () => ({ style: { minWidth: 140, maxWidth: 140 } }),
    //   onCell: () => ({ style: { minWidth: 140, maxWidth: 140 } }),
    //   render: val => val || 0,
    // },
    ...(opportunityData.statusTotals || []).map(st => {
      const childNodes = statusChildrenMap[st.statusId] || [];
      const hasChildren = childNodes.length > 0;
      const isActiveColumn = activeColumnFilter?.parentStatusId === st.statusId;
      const selectedChildKeys = isActiveColumn ? activeColumnFilter.selectedChildIds : [];
      // Per backend spec, the dynamic status column sorts by the literal
      // `statusName` string (e.g. "0 - Unqualified") — so use statusName as the
      // column key (which AntD will send back as sorter.columnKey).
      return {
        title: (
          <span className="block text-center leading-tight break-words whitespace-normal">
            {st.statusName}
          </span>
        ),
        key: st.statusName,
        align: "center",
        width: 150,
        sorter: true,
        sortOrder: getSortOrder(st.statusName),
        onHeaderCell: () => ({ style: { minWidth: 150, maxWidth: 150 } }),
        onCell: () => ({ style: { minWidth: 150, maxWidth: 150 } }),
        render: (_, record) => {
          const match = (record.statusCounts || []).find(sc => sc.statusId === st.statusId);
          const value = match?.totalCount || 0;
          // Zero counts stay plain text so users don't click into an empty list.
          if (value > 0) {
            return (
              <CustomButton
                type="link"
                className="!p-0 !h-auto"
                btnText={value}
                handleClick={() => goToOpportunities(record, st)}
              />
            );
          }
          return value;
        },
        ...(hasChildren && {
          filterDropdown: () => (
            <StatusColumnFilter
              childNodes={childNodes}
              value={selectedChildKeys}
              // Only one column filter can be active at a time — selecting on a new
              // column replaces any prior selection on a different column.
              onChange={keys =>
                setActiveColumnFilter(
                  keys?.length ? { parentStatusId: st.statusId, selectedChildIds: keys } : null
                )
              }
            />
          ),
          // AntD paints the filter icon blue when `filteredValue` is a non-empty
          // array — a boolean `filtered` alone doesn't trigger the color change.
          filteredValue: isActiveColumn && selectedChildKeys.length ? selectedChildKeys : null,
          // Filtering happens server-side via columnStatusIds — no client-side filter work.
          onFilter: () => true,
        }),
      };
    }),
  ];

  const isAppointment = viewType === VIEW_TYPES.APPOINTMENT;
  const isOpportunity = viewType === VIEW_TYPES.OPPORTUNITY;
  const isMessages = viewType === VIEW_TYPES.MESSAGES;
  const isActivities = viewType === VIEW_TYPES.ACTIVITIES;

  // --- Opportunity column visibility wiring ---
  // Columns surfaced in the Show/Hide Columns drawer. `expand` and the `User`
  // column are intentionally excluded — the row-expand icon and the user
  // identity must always remain visible (mirrors the Media Detail Report,
  // where Username is non-hideable). Titles in `opportunityColumns` are JSX so
  // we build a parallel array with plain-string titles for the drawer.
  const oppDrawerColumns = useMemo(() => {
    return opportunityColumns
      .filter(col => col.key !== "expand" && col.key !== ORDER_BY_FIELDS.FULL_NAME)
      .map(col => ({
        key: col.key,
        title: String(col.key),
      }));
  }, [opportunityColumns]);

  const oppDefaultColumnKeys = useMemo(() => oppDrawerColumns.map(c => c.key), [oppDrawerColumns]);

  // First load — seed visibility with all columns. Later, when the API returns
  // new dynamic status columns, merge only the genuinely new keys in (those
  // not present in the previously-seen default set). Comparing against
  // `prev` would re-add any column the user just hid, since the user's
  // selection differs from the full default set by design.
  useEffect(() => {
    if (!oppDefaultColumnKeys.length) return;
    const prevDefaults = prevOppDefaultsRef.current;
    const prevDefaultsSet = new Set(prevDefaults);
    const newlyArrivedKeys = oppDefaultColumnKeys.filter(k => !prevDefaultsSet.has(k));
    prevOppDefaultsRef.current = oppDefaultColumnKeys;
    setOppVisibleColumnKeys(prev => {
      if (!prev.length) return oppDefaultColumnKeys;
      if (!newlyArrivedKeys.length) return prev;
      return [...prev, ...newlyArrivedKeys];
    });
  }, [oppDefaultColumnKeys]);

  const handleToggleOppColumns = useCallback(() => {
    setOppTempVisibleColumnKeys(oppVisibleColumnKeys);
    setIsOppColumnDrawerVisible(true);
  }, [oppVisibleColumnKeys]);

  const handleOppColumnVisibilityChange = useCallback(selected => {
    setOppTempVisibleColumnKeys(selected);
  }, []);

  const handleApplyOppColumnChanges = useCallback(() => {
    setOppVisibleColumnKeys(oppTempVisibleColumnKeys);
    setIsOppColumnDrawerVisible(false);
  }, [oppTempVisibleColumnKeys]);

  const handleResetOppColumnDefaults = useCallback(() => {
    setOppTempVisibleColumnKeys(oppDefaultColumnKeys);
  }, [oppDefaultColumnKeys]);

  // Columns actually rendered in the table — `expand` and `User` are always
  // shown (non-hideable), the rest gated by the user's visibility selection.
  const oppFilteredColumns = useMemo(() => {
    const visible = new Set(oppVisibleColumnKeys);
    return opportunityColumns.filter(
      col => col.key === "expand" || col.key === ORDER_BY_FIELDS.FULL_NAME || visible.has(col.key)
    );
  }, [opportunityColumns, oppVisibleColumnKeys]);

  // Summary cells must match the visible dynamic status columns (key === statusName).
  const oppVisibleStatusTotals = useMemo(() => {
    const visible = new Set(oppVisibleColumnKeys);
    return (opportunityData.statusTotals || []).filter(st => visible.has(st.statusName));
  }, [opportunityData.statusTotals, oppVisibleColumnKeys]);

  // User column is always rendered now (non-hideable), so its summary "Total"
  // cell always shows.
  const isOppUserColumnVisible = true;

  // --- Messages by Sales Person columns (live API) ---
  // `key` matches the backend `orderBy` value, `dataIndex` is the response
  // field. Both channels now break down sends into Otto + AI-Assisted +
  // AI-Accepted + AI-Modified + Manual. AI-Assisted is the roll-up sum of
  // Accepted (AI text sent as-is) + Modified (AI draft edited then sent), so
  // it's shown alongside its two parts.
  const MESSAGE_COLS = [
    // Text channel (4 visible columns; AI Accepted/Modified are commented out
    // below, drop the comments to re-enable a full 6-column Text group).
    { key: ORDER_BY_FIELDS.TEXT_OTTO_SENT, dataIndex: "textOttoSent", title: "Otto sent" },
    {
      key: ORDER_BY_FIELDS.TEXT_AI_ASSISTED_SENT,
      dataIndex: "textAiAssistedSent",
      title: "AI assisted sent",
    },
    // {
    //   key: ORDER_BY_FIELDS.TEXT_AI_ACCEPTED_SENT,
    //   dataIndex: "textAiAcceptedSent",
    //   title: "AI accepted sent",
    // },
    // {
    //   key: ORDER_BY_FIELDS.TEXT_AI_MODIFIED_SENT,
    //   dataIndex: "textAiModifiedSent",
    //   title: "AI modified sent",
    // },
    { key: ORDER_BY_FIELDS.TEXT_MANUAL_SENT, dataIndex: "textManualSent", title: "Manual sent" },
    { key: ORDER_BY_FIELDS.TEXT_RECEIVED, dataIndex: "textReceived", title: "Received" },
    // Email channel (4 visible columns; Email AI Accepted/Modified commented
    // out — re-enable alongside the text ones above.)
    { key: ORDER_BY_FIELDS.EMAIL_OTTO_SENT, dataIndex: "emailOttoSent", title: "Otto sent" },
    {
      key: ORDER_BY_FIELDS.EMAIL_AI_ASSISTED_SENT,
      dataIndex: "emailAiAssistedSent",
      title: "AI assisted sent",
    },
    // {
    //   key: ORDER_BY_FIELDS.EMAIL_AI_ACCEPTED_SENT,
    //   dataIndex: "emailAiAcceptedSent",
    //   title: "AI accepted sent",
    // },
    // {
    //   key: ORDER_BY_FIELDS.EMAIL_AI_MODIFIED_SENT,
    //   dataIndex: "emailAiModifiedSent",
    //   title: "AI modified sent",
    // },
    { key: ORDER_BY_FIELDS.EMAIL_MANUAL_SENT, dataIndex: "emailManualSent", title: "Manual sent" },
    { key: ORDER_BY_FIELDS.EMAIL_RECEIVED, dataIndex: "emailReceived", title: "Received" },
    // Call channel (2 columns)
    { key: ORDER_BY_FIELDS.CALL_INBOUND, dataIndex: "callInbound", title: "Inbound" },
    { key: ORDER_BY_FIELDS.CALL_OUTBOUND, dataIndex: "callOutbound", title: "Outbound" },
  ];

  // Column-visibility for the Messages view. Two Sets so the drawer's
  // Apply / Reset commit explicitly on click — never as a side-effect of
  // checking a checkbox.
  //   - `visibleMessageColKeys` → what the table currently renders.
  //   - `tempVisibleMessageColKeys` → the drawer's pending selection,
  //     seeded from the applied Set every time the drawer opens.
  const allMessageColKeys = useMemo(() => MESSAGE_COLS.map(c => c.key), []);
  const [visibleMessageColKeys, setVisibleMessageColKeys] = useState(
    () => new Set(allMessageColKeys)
  );
  const [tempVisibleMessageColKeys, setTempVisibleMessageColKeys] = useState(
    () => new Set(allMessageColKeys)
  );
  useEffect(() => {
    if (columnPanelOpen) setTempVisibleMessageColKeys(new Set(visibleMessageColKeys));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnPanelOpen]);
  const visibleMessageCols = MESSAGE_COLS.filter(c => visibleMessageColKeys.has(c.key));
  const isTextCol = k =>
    [
      ORDER_BY_FIELDS.TEXT_OTTO_SENT,
      ORDER_BY_FIELDS.TEXT_AI_ASSISTED_SENT,
      ORDER_BY_FIELDS.TEXT_MANUAL_SENT,
      ORDER_BY_FIELDS.TEXT_RECEIVED,
    ].includes(k);
  const isEmailCol = k =>
    [
      ORDER_BY_FIELDS.EMAIL_OTTO_SENT,
      ORDER_BY_FIELDS.EMAIL_AI_ASSISTED_SENT,
      ORDER_BY_FIELDS.EMAIL_MANUAL_SENT,
      ORDER_BY_FIELDS.EMAIL_RECEIVED,
    ].includes(k);
  const isCallCol = k => [ORDER_BY_FIELDS.CALL_INBOUND, ORDER_BY_FIELDS.CALL_OUTBOUND].includes(k);

  const goToMessageDetailFromSalesPersonCell = (record, dataIndex) => {
    const userId = record?.user?.id != null ? +record.user.id : null;
    const sel = SALES_MSG_COL_TO_DETAIL[dataIndex] || {};
    goToMessageDetail(MESSAGE_REPORT_SOURCE.BY_SALES_PERSON, dispatch, {
      ...(Number.isFinite(userId) ? { userIds: [userId] } : {}),
      ...(sel.tab ? { activeTab: sel.tab } : {}),
      ...(sel.segment ? { activeSegment: sel.segment } : {}),
      ...(sel.filterBox ? { activeFilterBox: sel.filterBox } : {}),
    });
  };

  const makeMessageLeafCol = col => ({
    title: (
      <span className="block text-center leading-tight break-words whitespace-normal">
        {col.title}
      </span>
    ),
    dataIndex: col.dataIndex,
    key: col.key,
    width: 130,
    align: "center",
    sorter: true,
    sortOrder: getSortOrder(col.key),
    render: (val, record) => {
      const numeric = Number(val) || 0;
      if (numeric > 0) {
        return (
          <CustomButton
            type="link"
            className="!p-0 !h-auto"
            btnText={numeric}
            handleClick={() => goToMessageDetailFromSalesPersonCell(record, col.dataIndex)}
          />
        );
      }
      return numeric;
    },
  });

  // Grouped column structure — AntD renders a two-row header (Text / Email /
  // Call on top, leaf labels underneath) automatically when `children` exists.
  const messagesColumns = [
    {
      title: "User",
      key: ORDER_BY_FIELDS.FULL_NAME,
      width: 200,
      fixed: "left",
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.FULL_NAME),
      render: (_, record) => {
        const fullName = [record?.user?.firstName, record?.user?.lastName]
          .filter(Boolean)
          .join(" ");
        return (
          <span className="flex-row-centered gap-1">
            <CustomAvatar name={fullName || record?.user?.email || ""} bgClass="bg-grey-700" />
            <Tooltip title={fullName || ""}>
              <CheckoutReportUserLink
                userId={record?.user?.id}
                name={fullName || ""}
                className="line-clamp-2 break-words leading-tight max-w-[140px]"
              />
            </Tooltip>
          </span>
        );
      },
    },
    ...(visibleMessageCols.some(c => isTextCol(c.key))
      ? [
          {
            title: <span className="block text-center">Text</span>,
            key: "text-group",
            children: visibleMessageCols.filter(c => isTextCol(c.key)).map(makeMessageLeafCol),
          },
        ]
      : []),
    ...(visibleMessageCols.some(c => isEmailCol(c.key))
      ? [
          {
            title: <span className="block text-center">Email</span>,
            key: "email-group",
            children: visibleMessageCols.filter(c => isEmailCol(c.key)).map(makeMessageLeafCol),
          },
        ]
      : []),
    ...(visibleMessageCols.some(c => isCallCol(c.key))
      ? [
          {
            title: <span className="block text-center">Call</span>,
            key: "call-group",
            children: visibleMessageCols.filter(c => isCallCol(c.key)).map(makeMessageLeafCol),
          },
        ]
      : []),
  ];

  // --- Activities by Sales Person columns (live API) ---
  // `key` matches the backend `orderBy` value, `dataIndex` is the response field.
  const ACTIVITY_COLS = [
    { key: ORDER_BY_FIELDS.CREATED_COUNT, dataIndex: "createdCount", title: "Created" },
    { key: ORDER_BY_FIELDS.ASSIGNED_COUNT, dataIndex: "assignedCount", title: "Assigned" },
    { key: ORDER_BY_FIELDS.UPCOMING, dataIndex: "upcoming", title: "Upcoming" },
    { key: ORDER_BY_FIELDS.OVERDUE, dataIndex: "overdue", title: "Overdue" },
    { key: ORDER_BY_FIELDS.COMPLETED, dataIndex: "completed", title: "Completed" },
  ];
  const activitiesColumns = [
    {
      title: "User",
      key: ORDER_BY_FIELDS.FULL_NAME,
      width: 200,
      fixed: "left",
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.FULL_NAME),
      render: (_, record) => {
        const fullName = [record?.user?.firstName, record?.user?.lastName]
          .filter(Boolean)
          .join(" ");
        return (
          <span className="flex-row-centered gap-1">
            <CustomAvatar name={fullName || record?.user?.email || ""} bgClass="bg-grey-700" />
            <Tooltip title={fullName || ""}>
              <CheckoutReportUserLink
                userId={record?.user?.id}
                name={fullName || ""}
                className="ellipsis max-w-[140px]"
              />
            </Tooltip>
          </span>
        );
      },
    },
    ...ACTIVITY_COLS.map(col => ({
      title: (
        <span className="block text-center leading-tight break-words whitespace-normal">
          {col.title}
        </span>
      ),
      dataIndex: col.dataIndex,
      key: col.key,
      width: 130,
      align: "center",
      sorter: true,
      sortOrder: getSortOrder(col.key),
      render: val => val || 0,
    })),
  ];

  const VIEW_LABELS = {
    [VIEW_TYPES.APPOINTMENT]: "Appointment",
    [VIEW_TYPES.OPPORTUNITY]: "Opportunity",
    [VIEW_TYPES.MESSAGES]: "Communication",
    [VIEW_TYPES.ACTIVITIES]: "Activities",
  };
  const metricDetails = isOpportunity
    ? OPPORTUNITY_BY_SALES_PERSON
    : isMessages
    ? COMMUNICATION_BY_SALES_PERSON
    : isActivities
    ? ACTIVITY_BY_SALES_PERSON
    : APPOINTMENTS_BY_SALES_PERSON;
  // Current Status toggle is shown only for Appointment + Opportunity (those
  // queries accept viewMode). Messages and Activities don't.
  const showCurrentStatusToggle = isAppointment || isOpportunity;
  // Created-on / Date-basis filter is shown for Appointment only — the
  // activities query has no dateType parameter, and messages/opportunity
  // use their own filter.
  const showDateBasisFilter = isAppointment;

  return (
    <div className="flex-col-centered gap-5">
      {!hideMetricHeader && (
        <MetricHeader
          title={
            <span className="flex-row-centered gap-1 dashboard-title-label">
              {lockedViewType ? (
                <span className="text-brand-500">{VIEW_LABELS[viewType]}</span>
              ) : (
                <Dropdown
                  menu={{
                    items: [
                      { label: "Appointment", key: VIEW_TYPES.APPOINTMENT },
                      { label: "Opportunity", key: VIEW_TYPES.OPPORTUNITY },
                      { label: "Communication", key: VIEW_TYPES.MESSAGES },
                      { label: "Activities", key: VIEW_TYPES.ACTIVITIES },
                    ],
                    onClick: e => {
                      setViewType(e.key);
                      setparams(prev => ({ ...prev, page: 1 }));
                      // Each view has a different set of sortable columns; clear
                      // the previous sort to avoid sending an unsupported orderBy.
                      onSortChange(null);
                    },
                  }}
                >
                  <span className="flex-row-centered gap-1 cursor-pointer text-brand-500">
                    {VIEW_LABELS[viewType]}
                    <DownIcon />
                  </span>
                </Dropdown>
              )}
              by Sales Person
            </span>
          }
          metricDetails={metricDetails}
        >
          {isMessages && !hideFullscreenButton && (
            <CustomButton
              type="link"
              btnText="View in fullscreen"
              className="p-0"
              handleClick={() =>
                navigateWithQuery(
                  ROUTES.communicationDetailReport,
                  { tab: COMMUNICATION_DETAIL_TABS.SALES_PERSON },
                  navigate
                )
              }
            />
          )}
          {showCurrentStatusToggle && (
            <span className="flex-row-centered gap-2 mr-4 text-sm">
              <Switch
                size="small"
                checked={viewMode === VIEW_MODES.NORMAL}
                onChange={checked =>
                  setViewMode(checked ? VIEW_MODES.NORMAL : VIEW_MODES.WATERFALL)
                }
              />
              Current Status
            </span>
          )}
          {showDateBasisFilter && (
            <>
              <DateBasisDropdown value={dateBasis} onChange={setDateBasis} />
              <FilterPeriodLabel filterType={filterType} dateRange={dateRangeDisplay} />
            </>
          )}
          {isOpportunity && (
            <>
              <OpportunityDateBasisDropdown
                value={opportunityDateBasis}
                onChange={setOpportunityDateBasis}
              />
              <FilterPeriodLabel filterType={filterType} dateRange={dateRangeDisplay} />
              <CustomButton
                aria-label="Show/Hide Columns"
                icon={<LuColumns3 size={16} />}
                handleClick={handleToggleOppColumns}
                tooltipTitle="Show / Hide Columns"
              />
            </>
          )}
        </MetricHeader>
      )}

      {isMessages && (
        <CustomTable
          bordered
          data={messagesData.data}
          loading={messagesLoader}
          columns={messagesColumns}
          rowKey={(r, idx) => `messages-${r?.user?.id ?? idx}`}
          // className="ellipsis-table"
          scroll={{ x: "max-content", y: 400 }}
          onChange={(_, __, sorter) => onSortChange(sorter)}
          pagination={buildTablePagination({
            total: messagesData.count,
            page: params.page,
            pageSize: params.pageSize,
            setParamsFn: setparams,
            override: useCommonPagination ? COMMON_PAGINATION : undefined,
          })}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row className="bg-grey-50 font-semibold">
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                {MESSAGE_COLS.map((c, idx) => (
                  <Table.Summary.Cell key={c.key} index={idx + 1} align="center">
                    {messagesData.totals?.[c.dataIndex] || 0}
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      )}
      {isActivities && (
        <CustomTable
          bordered
          data={activitiesData.data}
          columns={activitiesColumns}
          loading={activitiesLoader}
          rowKey={(r, idx) => `activities-${r?.user?.id ?? idx}`}
          // className="ellipsis-table"
          scroll={{ x: "max-content", y: 400 }}
          onChange={(_, __, sorter) => onSortChange(sorter)}
          pagination={buildTablePagination({
            total: activitiesData.count,
            page: params.page,
            pageSize: params.pageSize,
            setParamsFn: setparams,
          })}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row className="bg-grey-50 font-semibold">
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                {ACTIVITY_COLS.map((c, idx) => (
                  <Table.Summary.Cell key={c.key} index={idx + 1} align="center">
                    {activitiesData.totals?.[c.dataIndex] || 0}
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      )}
      {isAppointment && (
        <CustomTable
          bordered
          data={appointmentData.data}
          loading={salesPersonLoader}
          columns={appointmentColumns}
          // className="ellipsis-table"
          scroll={{ x: "max-content", y: 400 }}
          onChange={(_, __, sorter) => onSortChange(sorter)}
          pagination={buildTablePagination({
            total: appointmentData.count,
            page: params.page,
            pageSize: params.pageSize,
            setParamsFn: setparams,
          })}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row className="bg-grey-50 font-semibold">
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                {APPOINTMENT_STATUS_COLS.map((c, idx) => {
                  const total = appointmentData.totals?.[c.dataIndex] || 0;
                  const isTotalCol = c.dataIndex === "totalAppointments";
                  const canDrill = total > 0 && (c.apptStatus || isTotalCol);
                  return (
                    <Table.Summary.Cell key={c.key} index={idx + 1} align="center">
                      {canDrill ? (
                        <CustomButton
                          type="link"
                          className="!p-0 !h-auto"
                          btnText={total}
                          handleClick={() =>
                            goToAppointments(null, isTotalCol ? null : c.apptStatus, {
                              allStatuses: isTotalCol,
                            })
                          }
                        />
                      ) : (
                        total
                      )}
                    </Table.Summary.Cell>
                  );
                })}
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      )}
      {isOpportunity && (
        <CustomTable
          bordered
          data={opportunityData.data}
          loading={opportunityLoader}
          columns={oppFilteredColumns}
          rowKey="userId"
          className="ellipsis-table"
          scroll={{ x: "max-content", y: 400 }}
          onChange={(_, __, sorter) => onSortChange(sorter)}
          pagination={buildTablePagination({
            total: opportunityData.totalCount,
            page: params.page,
            pageSize: params.pageSize,
            setParamsFn: setparams,
          })}
          summary={() => {
            // Summary cells track the visible columns 1:1. Cell 0 sits under
            // the `expand` column; the "Total" label moves into cell 1 only
            // when the User column is visible (since it's the cell under it).
            let idx = 0;
            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-grey-50 font-semibold">
                  <Table.Summary.Cell index={idx++} />
                  {isOppUserColumnVisible && (
                    <Table.Summary.Cell index={idx++}>Total</Table.Summary.Cell>
                  )}
                  {oppVisibleStatusTotals.map(st => (
                    <Table.Summary.Cell key={st.statusId} index={idx++} align="center">
                      {st.totalCount || 0}
                    </Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      )}
      {isOppColumnDrawerVisible && (
        <ColumnVisibilityDrawer
          isVisible={isOppColumnDrawerVisible}
          onClose={() => setIsOppColumnDrawerVisible(false)}
          columns={oppDrawerColumns}
          visibleColumns={oppTempVisibleColumnKeys}
          defaultVisibleColumns={oppDefaultColumnKeys}
          onColumnVisibilityChange={handleOppColumnVisibilityChange}
          onApplyChanges={handleApplyOppColumnChanges}
          onResetDefaults={handleResetOppColumnDefaults}
        />
      )}
      {/* Messages-view column drawer — only used by the detail screen,
          gated by `columnPanelOpen`. Reuses the same drawer component
          the Opportunity view uses so the UX stays consistent. */}
      {isMessages && columnPanelOpen && (
        <ColumnVisibilityDrawer
          isVisible={columnPanelOpen}
          onClose={onCloseColumnPanel}
          columns={MESSAGE_COLS.map(c => ({ key: c.key, title: c.title }))}
          visibleColumns={[...tempVisibleMessageColKeys]}
          defaultVisibleColumns={allMessageColKeys}
          onColumnVisibilityChange={arr => setTempVisibleMessageColKeys(new Set(arr))}
          onApplyChanges={() => {
            setVisibleMessageColKeys(new Set(tempVisibleMessageColKeys));
            onCloseColumnPanel();
          }}
          onResetDefaults={() => {
            setVisibleMessageColKeys(new Set(allMessageColKeys));
            onCloseColumnPanel();
          }}
        />
      )}
    </div>
  );
};

export const LeadContactedBySource = () => {
  const [lang] = useLang();
  const [pieChart, setpieChart] = useState(false);
  const [leadContactedBySourceFilter, setLeadContactedBySourceFilter] = useState(LAST_30_DAYS);
  const {
    data: leadContactSourceData,
    refetch: refetchLeadContactSourceData,
    loading: leadContactedbySourceLoader,
  } = useQuery(GET_LEAD_CONTECTED_BY_SOURCE, {
    fetchPolicy: "network-only",
    variables: {
      filterType: leadContactedBySourceFilter,
    },
  });

  const leadContactedByStatus = leadContactSourceData?.getEngagementLeadAnalytics || [];
  const conductedLeads = filterByStatus(leadContactedByStatus)([
    "SENT",
    "OPT_PUT",
    "DELIVERED",
    "RESPONDED",
    "ENGAGED",
  ]);
  return (
    <GraphAnalysis
      handleGraphChanges={value => setpieChart(value)}
      graphChanger={pieChart}
      graphKey="leadContactedBySource"
      label={lang.leadContactedBySource}
      data={leadBySource(filterByRange(conductedLeads))}
      API={refetchLeadContactSourceData}
      filterType={leadContactedBySourceFilter}
      setFilterType={setLeadContactedBySourceFilter}
      loader={leadContactedbySourceLoader}
    />
  );
};
/**
 * ![Stats](https://img001.prntscr.com/file/img001/9l2wZtUgQKONgAa5EeZaJA.png)
 * @returns
 */
export const StatsBar = () => {
  const { filterType, dateRange, userIds, apiUserIds, apiTeamIds, opportunityTypeId } =
    useDashboardFilter();
  const [dashboardAnalyticsData, setdashboardAnalyticsData] = useState({
    totalActiveLeads: 0,
    totalNewLeads: 0,
    totalEngagedLeads: 0,
    percentageConversion: 0,
  });
  const { loading: getDashboardAnalyticsLoader } = useQuery(GET_DASHBOARD_ANALYTICS, {
    variables: {
      filterType,
      dateRange,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      opportunityTypeId,
    },
    onCompleted: res => {
      setdashboardAnalyticsData(res.getDashboardAnalytics);
    },
  });
  return (
    <div className="flex-row-centered w-full gap-4">
      <div
        // key={index}
        className="p-4 bg-white rounded-lg w-full flex-col-centered"
      >
        {/* <div className="flex-row-centered justify-end w-full">
          <FilterSelector filterType={statsTimeFrame} handleFilter={setstatsTimeFrame} />
        </div> */}
        <div className="w-full flex-row-centered justify-between">
          <div className="flex-col-centered gap-1 items-start">
            <span className="text-sm font-[400] text-grey-500 flex-row-centered gap-1">
              New Leads <SubMetricInfoIcon metricDetails={NEW_LEADS} />
            </span>
            <Skeleton loading={getDashboardAnalyticsLoader} active paragraph={{ rows: 0 }}>
              <span className="text-3xl font-[600] text-grey-700">
                {dashboardAnalyticsData.totalNewLeads || 0}
              </span>
            </Skeleton>
          </div>
          <div className="flex-col-centered gap-1 items-start">
            <span className="text-sm font-[400] text-grey-500 flex-row-centered gap-1">
              Engaged Leads <SubMetricInfoIcon metricDetails={ENGAGED_LEADS} />
            </span>
            <Skeleton loading={getDashboardAnalyticsLoader} active paragraph={{ rows: 0 }}>
              <span className="text-3xl font-[600] text-grey-700">
                {dashboardAnalyticsData.totalEngagedLeads || 0}
              </span>
            </Skeleton>
          </div>
          <div className="flex-col-centered gap-1 items-start">
            <span className="text-sm font-[400] text-grey-500 flex-row-centered gap-1">
              Engagement Rate <SubMetricInfoIcon metricDetails={ENGAGEMENT_RATE} />
            </span>
            <Skeleton loading={getDashboardAnalyticsLoader} active paragraph={{ rows: 0 }}>
              <span className="text-3xl font-[600] text-grey-700">
                {dashboardAnalyticsData.percentageConversion || 0}
              </span>
            </Skeleton>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * ![Leads engagement](https://img001.prntscr.com/file/img001/qwnh4_ryQvqPxlpVsqFynA.png)
 * ### [Figma Link](https://www.figma.com/design/NDVwkatBby4TFCN5oag0D1/1.-Web?node-id=12729-12724&t=ojvDzhXjpXYtiRcI-4)
 * @param {*} props
 * @returns
 */
export const LeadEngagement = () => {
  const {
    filterType,
    dateRange,
    dateRangeDisplay,
    userIds,
    apiUserIds,
    apiTeamIds,
    opportunityTypeId,
  } = useDashboardFilter();
  const dispatch = useDispatch();
  const [analytics, setanalytics] = useState({ data: [], count: 0 });
  // "Created on" vs "Updated on" — applied against the lead's
  // created_date / updated_date. Default Created on per product spec.
  // Mirrors the same dropdown used by Opportunity by Sales Person.
  const [leadDateBasis, setLeadDateBasis] = useState(DEFAULT_DATE_FILTER_TYPE);

  // Same opportunity-type expansion the other dashboard drill-ins use —
  // the activity-center query filters by exact leaf type ids, so a parent
  // alone wouldn't match any children. Expand into [parent + every
  // descendant] before dispatching.
  const { expandWithDescendants } = useOpportunityTypeTree();
  const { sortVariables, getSortOrder, onSortChange } = useColumnSort();
  const [params, setparams] = useState({
    page: 1,
    pageSize: 10,
  });

  useEffect(() => {
    setparams(prev => ({ ...prev, page: 1 }));
  }, [filterType, dateRange, userIds, leadDateBasis]);

  const { loading: leadEngagementAnalysisLoader } = useQuery(GET_LEAD_ENGAGEMENT_ANALYSIS, {
    fetchPolicy: "network-only",
    variables: {
      filterType,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      dateRange,
      opportunityTypeId,
      dateType: leadDateBasis,
      ...sortVariables,
      ...params,
    },
    onCompleted: res => {
      setanalytics(res.getLeadEngagementAnalysis);
    },
  });

  // Drill into Activity Center with the dashboard's scope pre-applied:
  // single quick-filter from the clicked tile/cell, user/team selection,
  // opportunity type, and the same date preset / custom range.
  //
  // `userIdsOverride` (optional) lets a per-user table cell click swap the
  // dashboard's apiUserIds for just that row's user, so the Activity Center
  // opens scoped to that one user.
  const goToActivityCenter = (quickFilter, userIdsOverride = null) => {
    // Drill-in fully overrides existing Activity Center state + clears the
    // previously selected lead so the right pane starts empty.
    dispatch(resetLeadFilters());
    dispatch(setSelectedLeadRedux({}));
    dispatch(setQuickFilters(quickFilter ? [quickFilter] : []));
    const effectiveUserIds = Array.isArray(userIdsOverride)
      ? userIdsOverride.map(Number)
      : Array.isArray(apiUserIds)
      ? apiUserIds.map(Number)
      : [];
    dispatch(setUserFilters(effectiveUserIds));
    // When a row-level user override fires, clear the team scope so the
    // result is strictly that single user (avoids the "user OR team"
    // ambiguity the BE would otherwise see).
    dispatch(
      setTeamFilters(userIdsOverride ? [] : Array.isArray(apiTeamIds) ? apiTeamIds.map(Number) : [])
    );
    // Expand into [parent + every descendant id] so the activity-center
    // BE filter (exact-id match per leaf type) returns rows for every
    // sub-type — matches what goToOpportunities / goToOpportunitiesFromSource
    // already do for the same reason.
    const oppTypeIds =
      opportunityTypeId != null
        ? expandWithDescendants(
            Array.isArray(opportunityTypeId) ? opportunityTypeId[0] : opportunityTypeId
          ).map(String)
        : [];
    dispatch(setOpportunityTypeFilters(oppTypeIds));
    // The chip count reflects PARENT picks, not the expanded descendant
    // list. Dashboard always sends a single parent type → count = 1 when
    // anything is sent, 0 otherwise.
    dispatch(setOpportunityTypeFilterCount(opportunityTypeId != null ? 1 : 0));
    // Date scope mirrors the activity-center modes — preset key alone, or
    // CUSTOM + populated [start, end], or neither.
    if (filterType === CUSTOM && Array.isArray(dateRange) && dateRange.length === 2) {
      dispatch(setDateFilterType(CUSTOM));
      dispatch(setLeadDateRange(dateRange));
    } else if (filterType) {
      dispatch(setDateFilterType(filterType));
      dispatch(setLeadDateRange([]));
    } else {
      dispatch(setDateFilterType(""));
      dispatch(setLeadDateRange([]));
    }
    // Carry the created-on / updated-on selection through to Activity
    // Center so the same date scope basis is honored there.
    dispatch(setLeadDateType(leadDateBasis));
    // Mark this as a dashboard drill-in so Activity Center surfaces the
    // "including deactivated users" banner. Cleared when the user
    // interacts with any filter (or leaves the AC).
    dispatch(setCameFromDashboard(true));
    navigate(ROUTES.activityCenter);
  };

  const tableColumns = [
    {
      title: "User",
      key: ORDER_BY_FIELDS.FULL_NAME,
      fixed: "left",
      width: 180,
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.FULL_NAME),
      render: (_, record) => {
        const fullName = record?.user?.fullName || "";
        return (
          <span className="flex-row-centered gap-1">
            <CustomAvatar name={fullName} bgClass="bg-grey-700" />
            <Tooltip title={fullName}>
              <CheckoutReportUserLink
                userId={record?.user?.id}
                name={fullName}
                className="line-clamp-2 break-words leading-tight max-w-[150px]"
              />
            </Tooltip>
          </span>
        );
      },
    },
    // Static columns — order is fixed, but titles read off the shared
    // `leadStatus` constant via `labelForQuickFilter(quickFilter)` so a
    // rename in the Activity Center filter list propagates here.
    ...[
      {
        dataIndex: "uncontacted",
        key: ORDER_BY_FIELDS.UNCONTACTED,
        quickFilter: QUICK_FILTERS.UNCONTACTED,
      },
      {
        dataIndex: "notResponded",
        key: ORDER_BY_FIELDS.NOT_RESPONDED,
        quickFilter: QUICK_FILTERS.NOT_RESPONDED,
      },
      {
        dataIndex: "responded",
        key: ORDER_BY_FIELDS.RESPONDED,
        quickFilter: QUICK_FILTERS.RESPONDED,
      },
      {
        dataIndex: "notEngaged",
        key: ORDER_BY_FIELDS.NOT_ENGAGED,
        quickFilter: QUICK_FILTERS.NOT_ENGAGED,
      },
      {
        dataIndex: "engaged",
        key: ORDER_BY_FIELDS.ENGAGED,
        quickFilter: QUICK_FILTERS.ENGAGED,
      },
      {
        dataIndex: "waitingForLeadReply",
        key: ORDER_BY_FIELDS.WAITING_FOR_LEAD_REPLY,
        quickFilter: QUICK_FILTERS.WAITING_FOR_REPLY,
      },
      {
        dataIndex: "needsYourReply",
        key: ORDER_BY_FIELDS.NEEDS_YOUR_REPLY,
        quickFilter: QUICK_FILTERS.NEEDS_YOUR_REPLY,
      },
      {
        dataIndex: "noResponseNeeded",
        key: ORDER_BY_FIELDS.NO_RESPONSE_NEEDED,
        quickFilter: QUICK_FILTERS.NO_RESPONSE_NEEDED,
      },
    ].map(col => ({
      title: (
        <span className="block text-center leading-tight break-words whitespace-normal">
          {labelForQuickFilter(col.quickFilter)}
        </span>
      ),
      dataIndex: col.dataIndex,
      key: col.key,
      align: "center",
      width: 140,
      onHeaderCell: () => ({ style: { minWidth: 140, maxWidth: 140 } }),
      onCell: () => ({ style: { minWidth: 140, maxWidth: 140 } }),
      sorter: true,
      sortOrder: getSortOrder(col.key),
      // Per-user cell click drills into the Activity Center with the row's
      // user pre-selected (in addition to the dashboard's other scope).
      // Zero counts stay plain — no point opening an empty list.
      render: (value, record) => {
        const count = value || 0;
        if (count > 0) {
          // Guard against missing / non-numeric user ids so we never
          // dispatch `[NaN]` as userFilters (BE silently drops it, which
          // looks like "userIds disappeared from the payload").
          const rawUserId = record?.user?.id;
          const userIdNum = Number(rawUserId);
          const userIdsOverride =
            rawUserId != null && Number.isFinite(userIdNum) ? [userIdNum] : null;
          return (
            <CustomButton
              type="link"
              className="!p-0 !h-auto"
              btnText={count}
              handleClick={() => goToActivityCenter(col.quickFilter, userIdsOverride)}
            />
          );
        }
        return count;
      },
    })),
  ];

  // Each metric tile drills into the Activity Center with one `quickFilter`
  // applied. Five tiles map to existing QUICK_FILTERS enum values; three
  // (Not Responded / Not Engaged / Needs Your Reply) use placeholder string
  // sentinels until the BE exposes real status names — replace the
  // placeholder values in `leadSlice.js > QUICK_FILTERS` when BE is ready.
  // Tiles read their labels from the shared `leadStatus` list (via
  // `labelForQuickFilter`) so renames in the Activity Center propagate.
  const summaryBoxes = [
    {
      metricDetails: UNCONTACTED,
      key: "uncontacted",
      quickFilter: QUICK_FILTERS.UNCONTACTED,
    },
    {
      metricDetails: NOT_RESPONDED,
      key: "notResponded",
      quickFilter: QUICK_FILTERS.NOT_RESPONDED,
    },
    {
      metricDetails: RESPONDED,
      key: "responded",
      quickFilter: QUICK_FILTERS.RESPONDED,
    },
    {
      metricDetails: NOT_ENGAGED,
      key: "notEngaged",
      quickFilter: QUICK_FILTERS.NOT_ENGAGED,
    },
    {
      metricDetails: ENGAGED,
      key: "engaged",
      quickFilter: QUICK_FILTERS.ENGAGED,
    },
    {
      metricDetails: WAITING_FOR_LEAD_REPLY,
      key: "waitingForLeadReply",
      quickFilter: QUICK_FILTERS.WAITING_FOR_REPLY,
    },
    {
      metricDetails: NEEDS_YOUR_REPLY,
      key: "needsYourReply",
      quickFilter: QUICK_FILTERS.NEEDS_YOUR_REPLY,
    },
    {
      metricDetails: NO_RESPONSE_NEEDED,
      key: "noResponseNeeded",
      quickFilter: QUICK_FILTERS.NO_RESPONSE_NEEDED,
    },
  ].map(box => ({ ...box, label: labelForQuickFilter(box.quickFilter) }));

  return (
    <div className="flex-col-centered gap-1">
      <MetricHeader title="Lead Engagement" metricDetails={LEAD_ENGAGEMENT_INFO} className="w-full">
        <OpportunityDateBasisDropdown value={leadDateBasis} onChange={setLeadDateBasis} />
        <FilterPeriodLabel filterType={filterType} dateRange={dateRangeDisplay} />
      </MetricHeader>
      {/* Summary — responsive grid: 2 cols (mobile), 4 cols (md) */}
      <div className="grid grid-cols-2 md:grid-cols-4 my-3 gap-2 w-full">
        {summaryBoxes.map(box => {
          const count = analytics?.[box.key] || 0;
          return (
            <div
              key={box.label}
              className="flex flex-col items-center justify-between p-3 border border-dashed border-gray-300 rounded"
            >
              <div className="flex items-center justify-center gap-1 w-full">
                <span className="text-xs text-gray-500 font-normal text-center leading-tight">
                  {box.label}
                </span>
                <span className="flex-shrink-0 flex items-center">
                  <SubMetricInfoIcon metricDetails={box.metricDetails} />
                </span>
              </div>
              <Skeleton loading={leadEngagementAnalysisLoader} active paragraph={{ rows: 0 }}>
                {count > 0 ? (
                  <CustomButton
                    type="link"
                    className="!p-0 !h-auto !text-lg !font-semibold mt-2"
                    btnText={count}
                    handleClick={() => goToActivityCenter(box.quickFilter)}
                  />
                ) : (
                  <span className="text-lg font-semibold text-grey-700 mt-2">{count}</span>
                )}
              </Skeleton>
            </div>
          );
        })}
      </div>
      <CustomTable
        columns={tableColumns}
        data={analytics.data}
        rowKey={record => record?.user?.id}
        loading={leadEngagementAnalysisLoader}
        scroll={{ x: "max-content", y: 400 }}
        onChange={(_, __, sorter) => onSortChange(sorter)}
        pagination={buildTablePagination({
          total: analytics.count,
          page: params.page,
          pageSize: params.pageSize,
          setParamsFn: setparams,
        })}
      />
    </div>
  );
};

const SOURCE_DATA_TYPES = [
  { label: "Appointment", key: "APPOINTMENT" },
  { label: "Opportunity", key: "LEAD_OPPORTUNITY" },
  { label: "Communication", key: "MESSAGES" },
];

// Roll-up / cross-status metrics that can't map to a single
// `appointmentStatus` enum. Total Appointments drills with ALL statuses
// selected (special-cased in `goToAppointmentsFromSource`); anything
// listed here still stays non-clickable.
const APPT_SOURCE_NON_CLICKABLE_METRICS = new Set(["sold"]);

// Metric-name keys the BE uses for the roll-up "Total Appointments"
// column in Appointment-by-Source. Compared against `total.name` in
// lowercase so any of these variants trigger the all-statuses drill.
const APPT_SOURCE_TOTAL_METRIC_KEYS = new Set(["total_appointments", "total_appointment"]);

export const SourceGraph = ({
  // Locks the dataset switcher (Appointment / Opportunity / Messages) to
  // this key — used by CommunicationDetailReport to embed just the
  // MESSAGES view without the dropdown.
  lockedSelectedType = null,
  // Hides the "View in fullscreen" button on the Messages tile — set
  // when this component is rendered inside the fullscreen detail screen.
  hideFullscreenButton = false,
  // Suppresses the entire MetricHeader — the detail screen provides its
  // own frame + filters and doesn't want the tile header repeated
  // inside each tab.
  hideMetricHeader = false,
  // Switches the table's pagination options to the app-wide
  // `COMMON_PAGINATION` shape when true. Dashboard callers leave this
  // false (SourceGraph renders unpaginated) — the detail screen sets it
  // so the source table matches the app's standard config.
  useCommonPagination = false,
  // Column-visibility hooks used by the detail screen. Parent lifts the
  // open state so the single "Columns" trigger in the filter bar can
  // drive whichever tab is currently active.
  columnPanelOpen = false,
  onCloseColumnPanel = () => {},
  // Detail screen owns its own source-filter picker in the filter bar —
  // when it commits, the parent passes the committed ids here so the
  // `GET_DATA_BY_SOURCE` payload picks them up. Dashboard leaves this
  // null so the internal column-filter state stays authoritative.
  sourceFilterIdsOverride = null,
  // Fired when the column drawer commits, so the detail screen's export
  // can ship the same columns the table is showing. Receives the applied
  // metric names, or null for "no narrowing — every column".
  onVisibleColumnsChange = () => {},
} = {}) => {
  const {
    filterType,
    dateRange,
    dateRangeDisplay,
    userIds,
    apiUserIds,
    apiTeamIds,
    opportunityTypeId,
  } = useDashboardFilter();
  const dispatch = useDispatch();

  // Same rationale as AppointmentBySalesPerson: a parent-type drill-in must
  // expand into [parent + every descendant] so the Lead Center opportunity
  // list (filtered by exact leaf type ids on leads) doesn't return zero.
  // `cache-first` makes this a no-op when the dashboard's cascader already
  // populated the cache.
  const { expandWithDescendants } = useOpportunityTypeTree();

  // Same source list the appointment page's OpportunitySourceSelect uses,
  // via the shared hook. `getIdsWithChildrenById(id)` returns
  // [clicked + every descendant key] in one shot, so a dashboard drill-in can
  // dispatch the full list without the dropdown re-discovering descendants
  // post-mount.
  const { getIdsWithChildrenById: getSourceIdsWithChildrenById } = useOpportunitySources();

  const [selectedType, setselectedType] = useState(lockedSelectedType || "APPOINTMENT");
  const [dateBasis, setDateBasis] = useState(DATE_BASIS.CREATED);
  // Opportunity has its own "Created on / Updated on" basis (mirrors the
  // dropdown used by Opportunity-by-Sales-Person). Kept separate from
  // `dateBasis` so switching tabs doesn't clobber the Appointment basis.
  const [opportunityDateBasis, setOpportunityDateBasis] = useState(OPPORTUNITY_DATE_BASIS.CREATED);
  // Mirrors the Appointment-by-Sales-Person waterfall/current-status switch:
  // applied to Appointment + Opportunity dataTypes only (Communication has
  // no waterfall concept). Defaults to Normal (Current Status) here — the
  // Source view is most useful as a "what's the state right now" snapshot.
  const [viewMode, setViewMode] = useState(VIEW_MODES.NORMAL);
  const [sourceData, setsourceData] = useState({ totals: [], data: [] });
  const { sortVariables, getSortOrder, onSortChange } = useColumnSort();

  const isAppointmentType = selectedType === "APPOINTMENT";
  const isOpportunityType = selectedType === "LEAD_OPPORTUNITY";
  const isMessagesType = selectedType === "MESSAGES";
  const showViewModeToggle = isAppointmentType || isOpportunityType;

  const sourceLocationId = sessionStorage.getItem("currentLocationId");
  const sourceLocationIds = useMemo(() => {
    if (!sourceLocationId) return [];
    return String(sourceLocationId)
      .split(",")
      .filter(Boolean)
      .map(id => +id);
  }, [sourceLocationId]);

  // Opportunity metric columns are status NAMES (e.g. "0 - Unqualified"), but
  // the opportunity view filters by status IDs. Fetch the status tree and build
  // a name → { id, allIds } map so a source-cell click can resolve the clicked
  // status name to its id plus every descendant id (the dashboard's count for a
  // status already rolls up its children).
  const { data: sourceStatusTreeRes } = useQuery(GET_OPPORTUNITY_STATUS_NEW, {
    fetchPolicy: "network-only",
    variables: { locationIds: sourceLocationIds, opportunityTypeId },
    skip: !isOpportunityType,
  });

  const statusNameToIdsMap = useMemo(() => {
    const map = {};
    const collectIds = node => {
      const ids = node?.key != null ? [+node.key] : [];
      (node?.childs || []).forEach(c => ids.push(...collectIds(c)));
      return ids;
    };
    const walk = nodes => {
      (nodes || []).forEach(n => {
        if (n?.name != null) {
          map[n.name] = { id: +n.key, allIds: collectIds(n) };
        }
        walk(n?.childs);
      });
    };
    walk(sourceStatusTreeRes?.getOpportunityStatusNew || []);
    return map;
  }, [sourceStatusTreeRes]);

  // Status-name → direct children list. Mirrors the `statusChildrenMap`
  // used by AppointmentBySalesPerson's opportunity column filter, but keyed
  // by name (the SourceGraph column key is `total.name`, not statusId).
  // Pass the raw child objects through ({key, name, childs}) so
  // StatusColumnFilter can read `name` for labels and `childs` for nested
  // levels — same shape it consumes on the sales-person side.
  const statusNameToChildrenMap = useMemo(() => {
    const map = {};
    const walk = nodes => {
      (nodes || []).forEach(n => {
        if (n?.name != null && n.childs?.length) {
          map[n.name] = n.childs;
        }
        walk(n?.childs);
      });
    };
    walk(sourceStatusTreeRes?.getOpportunityStatusNew || []);
    return map;
  }, [sourceStatusTreeRes]);

  // Single active column filter — replaces selection across columns, same
  // behavior as AppointmentBySalesPerson's `activeColumnFilter`. Child IDs
  // ride along to the BE as `columnStatusIds`.
  const [sourceActiveColumnFilter, setSourceActiveColumnFilter] = useState(null);
  // Sources column filter — array of selected source ids from the
  // hierarchical picker. Payload wiring for `getDataBySource` isn't
  // available yet; for now we `console.log` on select so the flow is
  // observable end-to-end. Swap the logger for a query variable once BE
  // exposes the filter arg.
  const [selectedSourceFilterIds, setSelectedSourceFilterIds] = useState([]);
  const sourceColumnStatusIds = useMemo(
    () => sourceActiveColumnFilter?.selectedChildIds || [],
    [sourceActiveColumnFilter]
  );

  // Shared by the table query and the export — EXPORT_DATA_BY_SOURCE takes
  // the same arguments as GET_DATA_BY_SOURCE, so building the payload once
  // keeps the exported file matching the table on screen.
  // Rebuilt each render exactly as it was when inlined below — Apollo
  // deep-compares variables, so a fresh object triggers no extra fetch.
  const sourceQueryVariables = {
    dataType: selectedType,
    filterType,
    userIds: apiUserIds,
    teamIds: apiTeamIds,
    dateRange,
    opportunityTypeId,
    ...(isAppointmentType ? { dateType: dateBasis } : {}),
    ...(isOpportunityType ? { dateType: opportunityDateBasis } : {}),
    ...(showViewModeToggle ? { viewMode } : {}),
    // Opportunity column filter — narrows the column's count to the
    // selected child statuses. Only applies when isOpportunityType.
    ...(isOpportunityType && sourceColumnStatusIds.length > 0
      ? { columnStatusIds: sourceColumnStatusIds }
      : {}),
    // Sources filter — parent-provided ids win when supplied (detail
    // screen's filter-bar picker), otherwise fall back to the internal
    // column-filter state (dashboard tile). Only sent when non-empty so
    // the payload stays clean at the "no filter" default.
    ...(() => {
      const ids =
        Array.isArray(sourceFilterIdsOverride) && sourceFilterIdsOverride.length > 0
          ? sourceFilterIdsOverride
          : selectedSourceFilterIds;
      return ids.length > 0 ? { opportunitySourceIds: ids } : {};
    })(),
    ...sortVariables,
  };

  const { loading: sourceLoader } = useQuery(GET_DATA_BY_SOURCE, {
    fetchPolicy: "network-only",
    variables: sourceQueryVariables,
    onCompleted: res => {
      setsourceData(res.getDataBySource || { totals: [], data: [] });
    },
  });

  // Export lives on the tile for Appointment and Opportunity. The
  // Communication view exports from its fullscreen detail screen instead,
  // where the column picker is available.
  const [fetchSourceExport, { loading: sourceExportLoading }] = useLazyQuery(
    EXPORT_DATA_BY_SOURCE,
    { fetchPolicy: "network-only" }
  );

  // Export preferences drawer (source depth + hide-zero-rows). The
  // preference is per-location, so it's disabled whenever "All locations"
  // is in scope — there's no single location to save the preference against.
  const [exportPrefOpen, setExportPrefOpen] = useState(false);
  const isAllLocations = isAllLocationSelected();
  const singleExportLocationId =
    !isAllLocations && sourceLocationIds.length === 1 ? sourceLocationIds[0] : null;

  // `format` is the EXPORT_FORMAT enum value ("CSV" | "XLSX") — normalised to
  // lowercase at the call because the API expects "csv" / "xlsx".
  const handleSourceExport = async (format = EXPORT_FORMAT.CSV) => {
    try {
      const res = await fetchSourceExport({
        variables: { ...sourceQueryVariables, exportType: String(format).toLowerCase() },
      });
      const payload = res?.data?.exportDataBySource;
      const exportRequest = payload?.exportRequest;
      // Non-2xx statusCode on the top-level payload means the request itself
      // errored (e.g. 400 validation) — show the returned message rather than
      // falling through to the in-progress toast.
      const statusCode = Number(payload?.statusCode);
      if (statusCode && statusCode >= 400) {
        message.error(payload?.message || "Export failed. Please try again.");
      } else if (exportRequest?.status === "COMPLETED" && exportRequest.fileUrl) {
        downloadFileFromUrl(exportRequest.fileUrl, exportRequest.fileName);
      } else if (exportRequest?.status === "FAILED") {
        message.error(exportRequest.errorMessage || "Export failed. Please try again.");
      } else {
        message.info("Export is in progress. You'll receive a notification when it's ready.");
      }
    } catch (e) {
      message.error(`Export failed: ${e.message}`);
    }
  };

  // Reset sort when the dataType (Appointment / Opportunity / Messages) changes
  // since each dataset exposes a different set of sortable columns on the
  // backend (e.g. APPOINTMENT supports `scheduled`, RESPONSE supports
  // `messages_sent`, LEAD_OPPORTUNITY supports OpportunityStatus.name values).
  const handleClick = key => {
    setselectedType(key);
    onSortChange(null);
    // Drop the column filter when switching datasets — Opportunity-only
    // and the active key wouldn't make sense under Appointment / Messages.
    setSourceActiveColumnFilter(null);
  };

  const selectedTypeLabel = useMemo(
    () => SOURCE_DATA_TYPES.find(t => t.key === selectedType)?.label || "Appointment",
    [selectedType]
  );

  // Recursively strip empty `children: []` so AntD hides the expand icon for leaf rows
  const cleanedData = useMemo(() => {
    const clean = items =>
      (items || []).map(item => {
        const { children, ...rest } = item;
        if (Array.isArray(children) && children.length > 0) {
          return { ...rest, children: clean(children) };
        }
        return rest;
      });
    return clean(sourceData.data);
  }, [sourceData.data]);

  // In the grouped Communication view the parent header already names the
  // channel (Text / Email / Call), so the leaf label drops the redundant
  // leading channel word: `text_otto_sent` → "Otto sent",
  // `email_received` → "Received", `call_inbound` → "Inbound". Every channel
  // key is now explicitly prefixed (`text_*` / `email_*` / `call_*`); only
  // non-channel metrics like `opportunities` pass through unchanged.
  const stripChannelPrefix = name => {
    const key = (name || "").toLowerCase();
    if (key.startsWith("text_") || key.startsWith("email_") || key.startsWith("call_")) {
      return name.split("_").slice(1).join("_") || name;
    }
    return name;
  };

  // Drill from a source-row appointment cell into the Appointments list, with
  // the source + status + view-mode pre-applied. Mirrors `goToAppointments`
  // in AppointmentBySalesPerson but is keyed off `record.sourceId` instead
  const goToAppointmentsFromSource = (record, metricName) => {
    // Single source of truth: `GET_LEAD_SOURCES_DS` via the shared hook
    // (same tree the appointment page's dropdown uses). Same query in both
    // places means every id we dispatch matches a node the dropdown can
    // render → no numeric chips, checkboxes line up under the clicked parent.
    const clickedId = record?.sourceId != null ? +record.sourceId : null;
    const sourceIds = clickedId != null ? getSourceIdsWithChildrenById(clickedId) : [];
    // Total-column drill sends every status; a single-status column sends
    // just its uppercased name.
    const isTotalMetric = APPT_SOURCE_TOTAL_METRIC_KEYS.has((metricName || "").toLowerCase());
    const appointmentStatuses = isTotalMetric
      ? APPOINTMENT_STATUSES
      : [(metricName || "").toUpperCase()];
    const isCustom = filterType === CUSTOM;
    const dr =
      Array.isArray(dateRangeDisplay) && dateRangeDisplay.length === 2
        ? dateRangeDisplay
        : dateRange;
    dispatch(
      reducerSetAppointmentFilters({
        // Carry the dashboard's current user/team selection straight through.
        // EntityFilter on the page mirrors whatever was active on the dashboard,
        // so users see the same scope they were viewing.
        entityMode: apiTeamIds && apiTeamIds.length > 0 ? "teams" : "users",
        activeFilterMode: apiTeamIds && apiTeamIds.length > 0 ? "teams" : "users",
        userIds: apiUserIds || [],
        teamIds: apiTeamIds || [],
        userMode: 1,
        appointmentStatus: appointmentStatuses,
        opportunitySourceIds: sourceIds,
        cameFromDashboard: true,
        opportunityTypeId: opportunityTypeId ?? null,
        viewMode,
        dateMode:
          dateBasis === DATE_BASIS.CREATED
            ? APPOINTMENT_DATE_MODE.CREATED_ON
            : APPOINTMENT_DATE_MODE.DUE_ON,
        dateType: isCustom ? APPOINTMENT_DATE_CUSTOM : filterType,
        customRange: isCustom
          ? {
              startDate: dayjs(dr?.[0]).format(DATE_TIME_FORMATS.YYYY_MM_DD),
              endDate: dayjs(dr?.[1]).format(DATE_TIME_FORMATS.YYYY_MM_DD),
            }
          : { startDate: null, endDate: null },
        page: 1,
        pageSize: 10,
      })
    );
    navigate(ROUTES.engagementsAppointment);
  };

  // Opportunity counterpart of goToAppointmentsFromSource: drill from a source
  // cell into Lead Center's Opportunity view, pre-filtered to that source(s) +
  // the clicked status. Status name is resolved to ids via statusNameToIdsMap.
  const goToOpportunitiesFromSource = (record, metricName) => {
    // Resolve [clicked + descendants] from the dropdown tree (same hook as
    // the appointment path) so Lead Center's source filter pre-checks line
    // up with the column ids and there's no ID-universe mismatch with the
    // dashboard's own aggregation payload.
    const clickedId = record?.sourceId != null ? +record.sourceId : null;
    const sourceIds = clickedId != null ? getSourceIdsWithChildrenById(clickedId) : [];
    const statusMatch = statusNameToIdsMap[metricName];
    const statusIds = statusMatch?.allIds || [];
    const dr =
      Array.isArray(dateRangeDisplay) && dateRangeDisplay.length === 2
        ? dateRangeDisplay
        : dateRange;
    dispatch(
      setOpportunityFilters({
        // Carry the dashboard's current user/team scope straight through.
        entityMode: apiTeamIds && apiTeamIds.length > 0 ? "teams" : "users",
        activeFilterMode: apiTeamIds && apiTeamIds.length > 0 ? "teams" : "users",
        teamIds: apiTeamIds || [],
        assignedUserIds: apiUserIds || [],
        userTypesOr: [],
        userTypesAnd: [],
        // The opportunity Source filter tree pre-checks by string keys (React
        // coerces TreeNode keys to strings, so the manual flow also stores
        // strings) — store as strings so the dropdown reflects this selection.
        opportunitySource: sourceIds.map(String),
        opportunityStatus: statusIds,
        // Expand a parent-type pick into [parent + every descendant] so the
        // Lead Center opportunity query (filter by exact leaf type IDs on
        // leads) returns rows for the children too. Strings because AntD
        // `checkedKeys` on the column filter compares as strings.
        opportunityType:
          opportunityTypeId != null
            ? expandWithDescendants(
                Array.isArray(opportunityTypeId) ? opportunityTypeId[0] : opportunityTypeId
              ).map(String)
            : [],
        viewMode,
        filterType,
        dateRange: dr,
        dateType: opportunityDateBasis,
        cameFromDashboard: true,
      })
    );
    dispatch(setForceOpportunityView(true));
    navigate(ROUTES.engagementsLeadCenter);
  };

  // Communication counterpart: clicking a MESSAGES cell drills into the
  // message-sent-vs-received detail page, pre-filtered to the clicked
  // source + every descendant, and pre-selecting the column's
  // tab/segment/filterBox via `resolveDetailSelectionFromMetric`. All
  // mapping logic lives in `engagementDashboardHelpers` so renames flow
  // from one place.
  const goToMessageDetailFromSource = (record, metricName) => {
    const clickedId = record?.sourceId != null ? +record.sourceId : null;
    const sourceIds = clickedId != null ? getSourceIdsWithChildrenById(clickedId) : [];
    const { tab, segment, filterBox } = resolveDetailSelectionFromMetric(metricName);
    goToMessageDetail(MESSAGE_REPORT_SOURCE.BY_SOURCE, dispatch, {
      opportunitySourceIds: sourceIds.map(Number),
      ...(tab ? { activeTab: tab } : {}),
      ...(segment ? { activeSegment: segment } : {}),
      ...(filterBox ? { activeFilterBox: filterBox } : {}),
    });
  };

  // Dynamic columns from API totals (same shape for Appointment / Opportunity / Messages).
  // The metric `name` returned by the backend is also the value sent back as
  // `orderBy` (see EAI-1136 spec), so we use it as the column key.
  const makeSourceLeafCol = (total, grouped = false) => {
    // Status column filter is opportunity-only and only meaningful when the
    // status has children (otherwise there's nothing to refine to).
    const childNodes =
      isOpportunityType && statusNameToChildrenMap[total.name]
        ? statusNameToChildrenMap[total.name]
        : [];
    const hasChildren = childNodes.length > 0;
    const isActiveColumn = sourceActiveColumnFilter?.parentStatusName === total.name;
    const selectedChildKeys = isActiveColumn ? sourceActiveColumnFilter.selectedChildIds : [];

    return {
      title: (
        <span className="block text-center leading-tight break-words whitespace-normal">
          {convertToTitleCase(grouped ? stripChannelPrefix(total.name) : total.name)}
        </span>
      ),
      key: total.name,
      width: 120,
      align: "center",
      sorter: true,
      sortOrder: getSortOrder(total.name),
      onHeaderCell: () => ({ style: { minWidth: 120, maxWidth: 120 } }),
      onCell: () => ({ style: { minWidth: 120, maxWidth: 120 } }),
      ...(hasChildren && {
        filterDropdown: () => (
          <StatusColumnFilter
            childNodes={childNodes}
            value={selectedChildKeys}
            // Only one column filter can be active at a time — picking on a
            // new column replaces any prior selection.
            onChange={keys =>
              setSourceActiveColumnFilter(
                keys?.length ? { parentStatusName: total.name, selectedChildIds: keys } : null
              )
            }
          />
        ),
        // AntD paints the filter icon blue when `filteredValue` is a
        // non-empty array — a boolean alone doesn't change the color.
        filteredValue: isActiveColumn && selectedChildKeys.length ? selectedChildKeys : null,
        // Server-side filtering — no client-side row filter work.
        onFilter: () => true,
      }),
      render: (_, record) => {
        const match = (record.metrics || []).find(m => m.name === total.name);
        const value = match?.value || 0;
        // Only the Appointment view drills into the appointment list; clicking
        // an Opportunity / Communication cell isn't a sensible navigation.
        // Roll-up columns (Total appointments, Sold) aren't a single status,
        // so they can't pre-filter the list — keep them plain text. Zero
        // counts also stay plain text so users don't click into empty pages.
        const isClickableAppointmentMetric =
          isAppointmentType && value > 0 && !APPT_SOURCE_NON_CLICKABLE_METRICS.has(total.name);
        if (isClickableAppointmentMetric) {
          return (
            <CustomButton
              type="link"
              className="!p-0 !h-auto"
              btnText={value}
              handleClick={() => goToAppointmentsFromSource(record, total.name)}
            />
          );
        }
        // Opportunity cells are clickable only when the column name resolves to a
        // real status id (roll-up columns like "Opportunities" aren't in the map).
        const isClickableOpportunityMetric =
          isOpportunityType && value > 0 && !!statusNameToIdsMap[total.name];
        if (isClickableOpportunityMetric) {
          return (
            <CustomButton
              type="link"
              className="!p-0 !h-auto"
              btnText={value}
              handleClick={() => goToOpportunitiesFromSource(record, total.name)}
            />
          );
        }
        // Communication (MESSAGES) cells drill into the message-detail page
        // pre-filtered to the clicked source branch. Coerce to Number so a
        // string-typed BE value (e.g. "5") still flips the > 0 check.
        if (isMessagesType && Number(value) > 0) {
          return (
            <CustomButton
              type="link"
              className="!p-0 !h-auto"
              btnText={value}
              handleClick={() => goToMessageDetailFromSource(record, total.name)}
            />
          );
        }
        return value;
      },
    };
  };

  // Communication view: bucket the dynamic columns into Text / Email / Call
  // groups so the table mirrors the two-row header used by "Communication by
  // Sales Person". Every channel key is now explicitly prefixed by the BE
  // (`text_*` / `email_*` / `call_*`). Non-channel keys like `opportunities`
  // fall into OTHER and render flat alongside the groups.
  const bucketSourceMetric = name => {
    const key = (name || "").toLowerCase();
    if (key.startsWith("email_")) return "EMAIL";
    if (key.startsWith("call_")) return "CALL";
    if (key.startsWith("text_")) {
      return "TEXT";
    }
    return "OTHER";
  };

  // Metric names temporarily hidden from the Communication view. Comment a
  // key out below to re-enable that column (mirrors the comment-out hide on
  // the Communication-by-Sales-Person side). Keys match the BE metric
  // `name` values returned by getDataBySource (dataType: MESSAGES).
  const SOURCE_HIDDEN_METRIC_KEYS = new Set([
    "text_ai_accepted_sent",
    "text_ai_modified_sent",
    "email_ai_accepted_sent",
    "email_ai_modified_sent",
  ]);
  const isSourceMetricVisible = name => !SOURCE_HIDDEN_METRIC_KEYS.has((name || "").toLowerCase());

  // User-driven column visibility (detail screen only). Two Sets so the
  // drawer's Apply / Reset commit explicitly on click — never as a side
  // effect of ticking a checkbox.
  //   - `visibleSourceCols` → what the table renders. Null = "no filter,
  //     show everything" (the default before the user ever narrows).
  //   - `tempVisibleSourceCols` → the drawer's pending selection,
  //     seeded from the applied Set every time the drawer opens.
  const [visibleSourceCols, setVisibleSourceCols] = useState(null);
  const [tempVisibleSourceCols, setTempVisibleSourceCols] = useState(null);
  const isSourceColVisible = name => !visibleSourceCols || visibleSourceCols.has(name);
  useEffect(() => {
    if (!columnPanelOpen) return;
    // Seed temp from applied — fall back to "everything visible" (all
    // currently-loaded metric names) when nothing's been narrowed yet.
    const applied =
      visibleSourceCols ||
      new Set(
        (sourceData.totals || []).filter(t => isSourceMetricVisible(t.name)).map(t => t.name)
      );
    setTempVisibleSourceCols(new Set(applied));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnPanelOpen]);

  // Reorder totals into the same visual sequence as the rendered columns
  // (OTHER → Text → Email → Call for the Communication view; original order
  // for Appointment / Opportunity). The summary row maps over `orderedTotals`
  // so each cell lines up under its own column header — without this the
  // counts would drift when buckets change the column order.
  const orderedTotals = useMemo(() => {
    const all = (sourceData.totals || []).filter(t => isSourceMetricVisible(t.name));
    if (selectedType !== "MESSAGES") return all;
    const buckets = { TEXT: [], EMAIL: [], CALL: [], OTHER: [] };
    all.forEach(total => {
      buckets[bucketSourceMetric(total.name)].push(total);
    });
    return [...buckets.OTHER, ...buckets.TEXT, ...buckets.EMAIL, ...buckets.CALL];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, sourceData.totals]);

  const groupedMessageColumns = useMemo(() => {
    if (selectedType !== "MESSAGES") return null;
    const buckets = { TEXT: [], EMAIL: [], CALL: [], OTHER: [] };
    (sourceData.totals || []).forEach(total => {
      if (!isSourceMetricVisible(total.name)) return;
      if (!isSourceColVisible(total.name)) return;
      const bucket = bucketSourceMetric(total.name);
      // Channel-grouped leaves drop the redundant prefix; OTHER (flat) keeps
      // its full label since it has no parent group header.
      buckets[bucket].push(makeSourceLeafCol(total, bucket !== "OTHER"));
    });
    return [
      // Non-channel metrics (Opportunities, etc.) render flat first so they
      // sit right after the Sources column, matching the legacy layout.
      ...buckets.OTHER,
      buckets.TEXT.length && {
        title: <span className="block text-center">Text</span>,
        key: "text-group",
        children: buckets.TEXT,
      },
      buckets.EMAIL.length && {
        title: <span className="block text-center">Email</span>,
        key: "email-group",
        children: buckets.EMAIL,
      },
      buckets.CALL.length && {
        title: <span className="block text-center">Call</span>,
        key: "call-group",
        children: buckets.CALL,
      },
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, sourceData.totals, sortVariables, visibleSourceCols]);

  const sourceColumns = [
    {
      title: "Sources",
      dataIndex: "sourceName",
      key: ORDER_BY_FIELDS.SOURCE_NAME,
      fixed: "left",
      width: 200,
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.SOURCE_NAME),
      // Hierarchical source picker is temporarily hidden — the BE arg on
      // `getDataBySource` isn't ready yet, and the client-side stub was
      // shipping the filter icon with no real filtering behind it. Restore
      // by uncommenting the `filterDropdown` / `filteredValue` / `onFilter`
      // block below once BE support lands.
      //
      filterDropdown: ({ confirm }) => (
        <div className="p-2" style={{ width: 280 }}>
          <OpportunitySourceSelect
            variant="inline"
            value={selectedSourceFilterIds}
            onChange={ids => {
              setSelectedSourceFilterIds(ids || []);
            }}
            showApplyButton
            // Close the AntD column-filter panel on Apply / Reset.
            // `confirm({ closeDropdown: true })` collapses the panel
            // without altering AntD's own `filteredValue` — the
            // component drives `filteredValue` from the picker state.
            onClose={() => confirm({ closeDropdown: true })}
          />
        </div>
      ),
      filteredValue: selectedSourceFilterIds.length ? selectedSourceFilterIds : null,
      onFilter: () => true,
      render: text => <span className="text-black-88 text-sm font-normal">{text}</span>,
    },
    ...(groupedMessageColumns || (sourceData.totals || []).map(makeSourceLeafCol)),
  ];

  return (
    <div>
      {!hideMetricHeader && (
        <MetricHeader
          title={
            <span className="flex-row-centered gap-1 dashboard-title-label">
              {lockedSelectedType ? (
                <span className="text-brand-500">{selectedTypeLabel}</span>
              ) : (
                <Dropdown
                  menu={{
                    items: SOURCE_DATA_TYPES,
                    onClick: e => handleClick(e.key),
                  }}
                >
                  <span className="flex-row-centered gap-1 cursor-pointer text-brand-500">
                    {selectedTypeLabel}
                    <DownIcon />
                  </span>
                </Dropdown>
              )}
              by Sources
            </span>
          }
          metricDetails={selectedType === "MESSAGES" ? SOURCE_GRAPH_MESSAGE : SOURCE_GRAPH}
          className="w-full"
        >
          {/* One wrapper so every control in this header is separated by the
              same gap. MetricHeader spaces its own children at gap-1, which
              is too tight next to a button — this keeps the override scoped
              to the source tile instead of changing MetricHeader for every
              dashboard tile. */}
          <div className="flex-row-centered gap-3">
            {isMessagesType && !hideFullscreenButton && (
              <CustomButton
                type="link"
                btnText="View in fullscreen"
                className="p-0"
                handleClick={() =>
                  navigateWithQuery(
                    ROUTES.communicationDetailReport,
                    { tab: COMMUNICATION_DETAIL_TABS.SOURCE },
                    navigate
                  )
                }
              />
            )}
            {showViewModeToggle && (
              /* No `mr-4` here — the wrapper's gap spaces every control
               equally, and an extra margin would break that. */
              <span className="flex-row-centered gap-2 text-sm">
                <Switch
                  size="small"
                  checked={viewMode === VIEW_MODES.NORMAL}
                  onChange={checked =>
                    setViewMode(checked ? VIEW_MODES.NORMAL : VIEW_MODES.WATERFALL)
                  }
                />
                Current Status
              </span>
            )}
            {isAppointmentType && (
              <>
                <DateBasisDropdown value={dateBasis} onChange={setDateBasis} />
                <FilterPeriodLabel filterType={filterType} dateRange={dateRangeDisplay} />
              </>
            )}
            {isOpportunityType && (
              <>
                <OpportunityDateBasisDropdown
                  value={opportunityDateBasis}
                  onChange={setOpportunityDateBasis}
                />
                <FilterPeriodLabel filterType={filterType} dateRange={dateRangeDisplay} />
              </>
            )}
            {/* Last in the header. Communication is excluded — it exports from
              its fullscreen detail screen, which also carries the column
              picker. */}
            {(isAppointmentType || isOpportunityType) && (
              // Space.Compact joins the primary Export button and the 3-dot
              // dropdown into one split-button unit with a shared border,
              // instead of two separated buttons.
              <Tooltip title={isAllLocations ? "Select a single location to export." : ""}>
                <Space.Compact>
                  <Button
                    icon={<LuDownload size={16} strokeWidth={1.75} />}
                    // Wrap so the click event isn't passed as `format` — that
                    // would stringify to "[object Object]" and reach the API
                    // as an unknown exportType.
                    onClick={() => handleSourceExport(EXPORT_FORMAT.CSV)}
                    loading={sourceExportLoading}
                    disabled={sourceExportLoading || isAllLocations}
                  >
                    Export
                  </Button>
                  <Dropdown
                    trigger={["click"]}
                    disabled={sourceExportLoading || isAllLocations}
                    menu={{
                      items: [
                        { key: EXPORT_FORMAT.CSV, label: `Export as ${EXPORT_FORMAT.CSV}` },
                        { key: EXPORT_FORMAT.XLSX, label: `Export as ${EXPORT_FORMAT.XLSX}` },
                        { type: "divider" },
                        {
                          key: "preferences",
                          icon: <LuSettings size={14} />,
                          label: "Export preferences",
                        },
                      ],
                      onClick: ({ key }) => {
                        if (key === "preferences") setExportPrefOpen(true);
                        else handleSourceExport(key);
                      },
                    }}
                  >
                    <Button
                      aria-label="Export options"
                      icon={<LuEllipsis size={16} />}
                      disabled={sourceExportLoading || isAllLocations}
                    />
                  </Dropdown>
                </Space.Compact>
              </Tooltip>
            )}
          </div>
        </MetricHeader>
      )}
      <div className="my-5">
        <CustomTable
          columns={sourceColumns}
          data={cleanedData}
          loading={sourceLoader}
          rowKey="sourceId"
          // Source table is unpaginated in both dashboard and detail screen —
          // `GET_DATA_BY_SOURCE` returns all rows in one payload and the
          // caller expects the full list, matching the outside tile.
          pagination={false}
          scroll={{ x: "max-content", y: 400 }}
          onChange={(_, __, sorter) => onSortChange(sorter)}
          expandable={{
            childrenColumnName: "children",
          }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row className="bg-grey-50 font-semibold">
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                {orderedTotals.map((total, idx) => {
                  const value = total.value || 0;
                  // Summary row is intentionally NOT clickable — the
                  // total-row drill would cover the whole dataset,
                  // which isn't a useful pre-filter. Cells in the data
                  // rows above still drill.
                  return (
                    <Table.Summary.Cell key={total.name} index={idx + 1} align="center">
                      {value}
                    </Table.Summary.Cell>
                  );
                })}
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </div>
      {/* Source-view column drawer — only used by the detail screen,
          gated by `columnPanelOpen`. Options come from the currently
          loaded metrics (filtered through `isSourceMetricVisible` so
          BE-hidden keys never surface). */}
      {isMessagesType && columnPanelOpen && (
        <ColumnVisibilityDrawer
          isVisible={columnPanelOpen}
          onClose={onCloseColumnPanel}
          columns={(sourceData.totals || [])
            .filter(t => isSourceMetricVisible(t.name))
            .map(t => ({ key: t.name, title: convertToTitleCase(t.name) }))}
          visibleColumns={tempVisibleSourceCols ? [...tempVisibleSourceCols] : []}
          defaultVisibleColumns={(sourceData.totals || [])
            .filter(t => isSourceMetricVisible(t.name))
            .map(t => t.name)}
          onColumnVisibilityChange={arr => setTempVisibleSourceCols(new Set(arr))}
          onApplyChanges={() => {
            const applied = new Set(tempVisibleSourceCols || []);
            setVisibleSourceCols(applied);
            onVisibleColumnsChange([...applied]);
            onCloseColumnPanel();
          }}
          onResetDefaults={() => {
            setVisibleSourceCols(null);
            onVisibleColumnsChange(null);
            onCloseColumnPanel();
          }}
        />
      )}
      <SourceExportPreferenceDrawer
        open={exportPrefOpen}
        onClose={() => setExportPrefOpen(false)}
        locationId={singleExportLocationId}
      />
    </div>
  );
};

export const MessageType = props => {
  const {
    filterType,
    dateRange,
    dateRangeDisplay,
    userIds,
    apiUserIds,
    apiTeamIds,
    opportunityTypeId,
  } = useDashboardFilter();
  const dispatch = useDispatch();
  const { sortVariables, getSortOrder, onSortChange } = useColumnSort();
  const [params, setparams] = useState({
    page: 1,
    pageSize: 10,
  });
  const [drawerState, setDrawerState] = useState({
    open: false,
    mediaType: null,
    userId: null,
    userName: "",
    count: 0,
  });

  const handleMediaCellClick = (record, mediaType, count) => {
    setDrawerState({
      open: true,
      mediaType,
      userId: record?.user?.id || null,
      userName: record?.user?.fullName || "",
      count: count || 0,
    });
  };

  // The dashboard's filters are already persisted to Redux under
  // "engagementDashboard" (see DashboardFilterContext). The detail page reads
  // those. When the user clicks a specific row's name we additionally stash a
  // one-shot override under MEDIA_TRACKING_REPORT_MODULE so the detail page
  // knows to pre-select that user.
  const goToDetailReport = (extraUserIds = null) => {
    const sessionLocId = sessionStorage.getItem("currentLocationId");
    dispatch(
      setModuleFilters({
        moduleKey: MEDIA_TRACKING_REPORT_MODULE,
        filters: {
          presetUserIds: Array.isArray(extraUserIds) && extraUserIds.length ? extraUserIds : null,
          // Only carry a concrete single-location id; the report needs exactly
          // one and "all locations" (csv) would leave it blank.
          presetLocationId:
            sessionLocId && !sessionLocId.includes(",") ? Number(sessionLocId) : null,
        },
      })
    );
    navigate(ROUTES.mediaTrackingDetailReport);
  };

  useEffect(() => {
    setparams(prev => ({ ...prev, page: 1 }));
  }, [filterType, dateRange, userIds]);

  const [messageTypeData, setmessageTypeData] = useState({
    data: [],
    count: 0,
  });
  const { loading: messageTypeLoader } = useQuery(GET_USER_MESSAGE_TYPE_ANALYSIS, {
    variables: {
      filterType,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      dateRange,
      opportunityTypeId,
      ...sortVariables,
      ...params,
    },
    onCompleted: res => {
      setmessageTypeData(res.getUserMessageTypeAnalysis);
    },
  });
  const columns = [
    {
      title: "User",
      key: ORDER_BY_FIELDS.FULL_NAME,
      fixed: "left",
      width: 160,
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.FULL_NAME),
      render: (text, record, index) => (
        <span className="flex-row-centered gap-1" key={index}>
          <CustomAvatar
            name={record?.user?.fullName}
            src={record?.user?.profilePic}
            bgClass="bg-grey-700"
          />
          <Tooltip title={record?.user?.fullName || ""}>
            <span
              className="line-clamp-2 break-words leading-tight text-twLinkColor cursor-pointer"
              title="View detailed media report for this user"
              onClick={() => record?.user?.id && goToDetailReport([record?.user?.id])}
            >
              {record?.user?.fullName || ""}
            </span>
          </Tooltip>
        </span>
      ),
    },
    {
      title: "Images",
      dataIndex: "imageMessages",
      key: ORDER_BY_FIELDS.IMAGE_MESSAGES,
      width: 100,
      align: "right",
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.IMAGE_MESSAGES),
      render: (value, record) => (
        <CustomButton
          type="link"
          size="small"
          className="!pr-0 !p-0 !h-auto !leading-none"
          btnText={value || 0}
          handleClick={() => handleMediaCellClick(record, "IMAGE", value)}
        />
      ),
    },
    {
      title: "Videos",
      dataIndex: "videoMessages",
      key: ORDER_BY_FIELDS.VIDEO_MESSAGES,
      width: 100,
      align: "right",
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.VIDEO_MESSAGES),
      render: (value, record) => (
        <CustomButton
          type="link"
          size="small"
          className="!pr-0 !p-0 !h-auto !leading-none"
          btnText={value || 0}
          handleClick={() => handleMediaCellClick(record, "VIDEO", value)}
        />
      ),
    },
    {
      title: "Docs",
      dataIndex: "docMessages",
      key: ORDER_BY_FIELDS.DOC_MESSAGES,
      width: 100,
      align: "right",
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.DOC_MESSAGES),
      render: (value, record) => (
        <CustomButton
          type="link"
          size="small"
          className="!pr-0 !p-0 !h-auto !leading-none"
          btnText={value || 0}
          handleClick={() => handleMediaCellClick(record, "DOC", value)}
        />
      ),
    },
    // {
    //   title: "Audio",
    //   dataIndex: "audioMessages",
    //   key: ORDER_BY_FIELDS.AUDIO_MESSAGES,
    //   width: 100,
    //   align: "right",
    //   sorter: true,
    //   sortOrder: getSortOrder(ORDER_BY_FIELDS.AUDIO_MESSAGES),
    // },
  ];

  return (
    <div className="flex-col-centered gap-2">
      <MetricHeader title="Media Tracking" metricDetails={MESSAGE_TYPE_INFO} titleElement="h2">
        <span
          className="text-twLinkColor cursor-pointer text-sm"
          onClick={() => goToDetailReport()}
        >
          View detailed report
        </span>
      </MetricHeader>
      <CustomTable
        bordered
        loading={messageTypeLoader}
        data={messageTypeData?.data}
        columns={columns}
        scroll={{ x: "max-content", y: 300 }}
        onChange={(_, __, sorter) => onSortChange(sorter)}
        pagination={buildTablePagination({
          total: messageTypeData.count,
          page: params.page,
          pageSize: params.pageSize,
          setParamsFn: setparams,
        })}
      />
      {drawerState.open && (
        <MessageTypeDetailDrawer
          open={drawerState.open}
          onClose={() => setDrawerState(prev => ({ ...prev, open: false }))}
          mediaType={drawerState.mediaType}
          userId={drawerState.userId}
          userName={drawerState.userName}
          count={drawerState.count}
          filterLabel={getFilterPeriodLabel(filterType, dateRangeDisplay)}
          filterType={filterType}
          dateRange={dateRange}
          opportunityTypeId={opportunityTypeId}
        />
      )}
    </div>
  );
};

export const ResponseTime = props => {
  const { filterType, dateRange, userIds, apiUserIds, apiTeamIds, opportunityTypeId } =
    useDashboardFilter();
  const { sortVariables, getSortOrder, onSortChange } = useColumnSort();
  const [params, setparams] = useState({
    page: 1,
    pageSize: 10,
  });

  useEffect(() => {
    setparams(prev => ({ ...prev, page: 1 }));
  }, [filterType, dateRange, userIds]);

  const [responseTimeData, setresponseTimeData] = useState({
    data: [],
    count: 0,
    averageBestTime: 0,
    averageWorstTime: 0,
    averageTime: 0,
    trend: "",
    percentageChange: 0,
    averageResponseTimeAfterNudge: 0,
  });
  const { loading: responseTimeLoader } = useQuery(GET_USER_MESSAGE_RESPONSE_TIME_ANALYSIS, {
    variables: {
      filterType,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      dateRange,
      opportunityTypeId,
      ...sortVariables,
      ...params,
    },
    onCompleted: res => {
      setresponseTimeData(res.getUserMessageResponseTimeAnalysis);
    },
  });
  const columns = [
    {
      title: "User",
      key: ORDER_BY_FIELDS.FULL_NAME,
      width: 200,
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.FULL_NAME),
      render: (text, record, index) => (
        <div key={index} className="flex-row-centered gap-1">
          <CustomAvatar
            name={record?.user?.fullName}
            src={record?.user?.profilePic}
            bgClass="bg-grey-700"
          />
          <Tooltip title={record?.user?.fullName || ""}>
            <CheckoutReportUserLink
              userId={record?.user?.id}
              name={record?.user?.fullName || ""}
              className="line-clamp-2 break-words leading-tight"
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: (
        <span className="flex-row-centered text-xs font-[400] text-grey-700 items-center justify-center p-1 rounded bg-success-100">
          Best Time
        </span>
      ),
      dataIndex: "bestTime",
      key: ORDER_BY_FIELDS.BEST_TIME,
      align: "center",
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.BEST_TIME),
      render: value => formatResponseTime(value),
    },
    {
      title: (
        <span className="flex-row-centered text-xs font-[400] text-grey-700 items-center justify-center p-1 rounded bg-red-100">
          Worst Time
        </span>
      ),
      dataIndex: "worstTime",
      key: ORDER_BY_FIELDS.WORST_TIME,
      align: "center",
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.WORST_TIME),
      render: value => formatResponseTime(value),
    },
    {
      title: (
        <span className="flex-row-centered text-xs font-[400] text-grey-700 items-center justify-center p-1 rounded bg-grey-100">
          Avg. Time
        </span>
      ),
      dataIndex: "averageTime",
      key: ORDER_BY_FIELDS.AVERAGE_TIME,
      align: "center",
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.AVERAGE_TIME),
      render: value => formatResponseTime(value),
    },
    {
      title: (
        <span className="flex-row-centered text-xs font-[400] text-grey-700 items-center justify-center p-1 rounded bg-blue-100">
          Median Time
        </span>
      ),
      dataIndex: "medianTime",
      key: ORDER_BY_FIELDS.MEDIAN_TIME,
      align: "center",
      sorter: true,
      sortOrder: getSortOrder(ORDER_BY_FIELDS.MEDIAN_TIME),
      render: value => formatResponseTime(value),
    },
  ];
  return (
    <div className="flex-col-centered gap-2">
      <MetricHeader title="Response Time" metricDetails={RESPONSE_TIME_INFO} titleElement="h2" />
      <div className="w-full mt-2 bg-grey-50">
        <div className="flex border border-solid border-gray-300">
          {/* <div className="flex flex-col w-1/3 items-center gap-1 p-3">
            <span className="text-sm text-gray-500 font-normal">Avg. Best Time</span>
            <Skeleton loading={responseTimeLoader} active paragraph={{ rows: 0 }}>
              <span className="text-base font-[600] text-grey-700">
                {convertMinutesToHour(responseTimeData.averageBestTime)} min
              </span>
            </Skeleton>
          </div>
          <div className="w-px border-gray-300 border border-solid" />
          <div className="flex flex-col w-1/3 items-center gap-1 p-3">
            <span className="text-sm text-gray-500 font-normal">Avg. Worst Time</span>
            <Skeleton loading={responseTimeLoader} active paragraph={{ rows: 0 }}>
              <span className="text-base font-[600] text-grey-700">
                {convertMinutesToHour(responseTimeData.averageWorstTime)} min
              </span>
            </Skeleton>
          </div> */}
          <div className="flex flex-col w-1/2 items-center gap-1 p-3">
            <span className="text-sm text-gray-500 font-normal flex items-center gap-1">
              Avg. Time (KPI: 20 Min)
              <SubMetricInfoIcon metricDetails={AVG_RESPONSE_TIME} />
            </span>
            <Skeleton loading={responseTimeLoader} active paragraph={{ rows: 0 }}>
              <div className="flex-row-centered gap-1 items-center">
                <span className="text-base font-[600] text-grey-700">
                  {formatResponseTime(responseTimeData?.averageTime)}
                </span>
                {responseTimeData?.percentageChange && (
                  <Tag
                    className="flex-row-centered items-center gap-0.5"
                    color={responseTimeData?.trend === "up" ? "green" : "red"}
                  >
                    {responseTimeData?.percentageChange !== "N/A"
                      ? `${responseTimeData?.percentageChange}%`
                      : ""}
                    {responseTimeData?.trend === "up" ? (
                      <DashboardArrow color="#22c55e" className="rotate-180" />
                    ) : (
                      <DashboardArrow />
                    )}
                  </Tag>
                )}
              </div>
            </Skeleton>
          </div>
          <div className="w-px border-gray-300 border border-solid" />
          <div className="flex flex-col w-1/2 items-center gap-1 p-3">
            <span className="text-sm text-gray-500 font-normal flex items-center gap-1">
              Avg. Response Time After Nudge
              <SubMetricInfoIcon metricDetails={AVG_RESPONSE_TIME_AFTER_NUDGE} />
            </span>
            <Skeleton loading={responseTimeLoader} active paragraph={{ rows: 0 }}>
              <span className="text-base font-[600] text-grey-700">
                {formatResponseTime(responseTimeData?.averageResponseTimeAfterNudge)}
              </span>
            </Skeleton>
          </div>
        </div>
      </div>
      <CustomTable
        bordered
        loading={responseTimeLoader}
        data={responseTimeData?.data}
        columns={columns}
        scroll={{ y: 300 }}
        onChange={(_, __, sorter) => onSortChange(sorter)}
        pagination={buildTablePagination({
          total: responseTimeData?.count,
          page: params.page,
          pageSize: params.pageSize,
          setParamsFn: setparams,
        })}
      />
    </div>
  );
};

export const ReviewRequestAnalysis = props => {
  const dispatch = useDispatch();
  const { filterType, dateRange, userIds, apiUserIds, apiTeamIds, opportunityTypeId } =
    useDashboardFilter();
  const [params, setparams] = useState({
    page: 1,
    pageSize: 6,
  });

  useEffect(() => {
    setparams(prev => ({ ...prev, page: 1 }));
  }, [filterType, dateRange, userIds]);

  const [reviewInviteAnalyticsData, setreviewInviteAnalyticsData] = useState({
    data: [],
    count: 0,
  });
  const { loading: userReviewAnalysisLoader } = useQuery(GET_USER_REVIEW_ANALYSIS, {
    variables: {
      filterType,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      dateRange,
      opportunityTypeId,
      ...params,
    },
    onCompleted: res => {
      setreviewInviteAnalyticsData(res.getUserReviewAnalysis);
    },
  });
  // Drill-in: write the clicked metric's status as a one-shot preset under
  // the "reviewDetailReport" module, then navigate. The detail page consumes
  // and clears the preset on mount. Outer dashboard filters (user/team,
  // opportunity type, date) are read from "engagementDashboard" directly.
  const goToReviewDetail = reviewStatus => {
    dispatch(
      setModuleFilters({
        moduleKey: "reviewDetailReport",
        filters: { reviewStatus },
      })
    );
    navigate(ROUTES.reviewDetailReport);
  };

  const inviteSentCount = reviewInviteAnalyticsData.totalInviteSents || 0;
  const reviewCompletedCount = reviewInviteAnalyticsData.totalReviewCompleted || 0;
  const inviteNotSentCount = reviewInviteAnalyticsData.totalInviteNotSents || 0;

  const renderMetricCount = (count, reviewStatus) => {
    if (!count) {
      return <span className="text-base font-[600] text-grey-700">{count}</span>;
    }
    return (
      <CustomButton
        type="link"
        className="!p-0 !h-auto !text-base !font-[600]"
        btnText={count}
        handleClick={() => goToReviewDetail(reviewStatus)}
      />
    );
  };

  return (
    <div>
      <MetricHeader
        title="Review Request Analysis"
        metricDetails={REVIEW_REQUEST_ANALYSIS_INFO}
        className="w-full"
      />
      <div className="flex justify-center mt-1">
        <div className="flex border border-dashed border-gray-300">
          <div className="flex flex-col items-center gap-1 p-3">
            <span className="text-sm text-gray-500 font-normal flex items-center gap-1">
              Invite Sent <SubMetricInfoIcon metricDetails={INVITE_SENT} />
            </span>
            <Skeleton loading={userReviewAnalysisLoader} active paragraph={{ rows: 0 }}>
              {renderMetricCount(inviteSentCount, REVIEW_STATUS.INVITE_SENT)}
            </Skeleton>
          </div>
          <div className="w-px border-gray-300 border border-dashed" />
          <div className="flex flex-col items-center gap-1 p-3">
            <span className="text-sm text-gray-500 font-normal flex items-center gap-1">
              Review Completed <SubMetricInfoIcon metricDetails={REVIEW_COMPLETED} />
            </span>
            <Skeleton loading={userReviewAnalysisLoader} active paragraph={{ rows: 0 }}>
              {renderMetricCount(reviewCompletedCount, REVIEW_STATUS.REVIEW_COMPLETED)}
            </Skeleton>
          </div>
          <div className="w-px border-gray-300 border border-dashed" />
          <div className="flex flex-col items-center gap-1 p-3">
            <span className="text-sm text-gray-500 font-normal flex items-center gap-1">
              Invite Not Sent <SubMetricInfoIcon metricDetails={INVITE_NOT_SENT} />
            </span>
            <Skeleton loading={userReviewAnalysisLoader} active paragraph={{ rows: 0 }}>
              {renderMetricCount(inviteNotSentCount, REVIEW_STATUS.INVITE_NOT_SENT)}
            </Skeleton>
          </div>
        </div>
      </div>
      <Skeleton className="h-[250px]" loading={userReviewAnalysisLoader} avatar active>
        <Column
          data={reviewInviteAnalyticsData.data.map(item => ({
            value: item.value,
            userFullName: item.user.fullName,
            name: convertToTitleCase(item.name),
          }))}
          xField="userFullName"
          yField="value"
          seriesField="name"
          isGroup
          columnStyle={{ radius: [20, 20, 20, 20] }}
          maxColumnWidth={10}
          className="mb-2"
          xAxis={{
            label: {
              formatter: text => {
                return text
                  .split(" ")
                  .map(word => word[0])
                  .join("");
              },
              // autoHide: false,
            },
          }}
          color={["#5B8FF9", "#5AD8A6", "#4e4d54"]}
        />
      </Skeleton>
      <AnalyticsPagination
        total={reviewInviteAnalyticsData.count}
        pageSize={params.pageSize}
        page={params.page}
        // className="mt-3"
        pageSizeOptions={[6, 10, 20, 50, 100]}
        length={reviewInviteAnalyticsData.data.length / 3}
        onChange={(page, pageSize) =>
          setparams(prev => ({ page: pageSize !== prev.pageSize ? 1 : page, pageSize }))
        }
      />
    </div>
  );
};

export const ReviewStatus = props => {
  const { redirectToLead } = useLeadRedirection();
  const dispatch = useDispatch();

  const { filterType, dateRange, userIds, apiUserIds, apiTeamIds, opportunityTypeId } =
    useDashboardFilter();
  const [reviewStatusData, setreviewStatusData] = useState({
    count: 0,
    data: [],
  });
  const [params, setparams] = useState({
    page: 1,
    pageSize: 6,
  });
  const [reviewStatus, setreviewStatus] = useState("");

  useEffect(() => {
    setparams(prev => ({ ...prev, page: 1 }));
  }, [filterType, dateRange, userIds]);

  const { loading: reviewStatusAnalysisLoader } = useQuery(GET_REVIEW_STATUS_ANALYSIS, {
    variables: {
      filterType,
      reviewStatus,
      userIds: apiUserIds,
      teamIds: apiTeamIds,
      dateRange,
      opportunityTypeId,
      ...params,
    },
    onCompleted: res => {
      setreviewStatusData(res.getReviewStatusAnalysis);
    },
  });
  const reviewStatusRender = key => {
    switch (key) {
      case "review_completed":
        return <span className="text-success-500 xs-400">{convertToTitleCase(key)}</span>;
      case "invites_sent":
        return <span className="text-orange-500 xs-400">Request sent</span>;
      case "invite_not_sent":
        return (
          <div className="flex-col-centered items-start gap-1">
            <span className="text-red-500 xs-400">Request Not Sent</span>
            {/* <Button disabled className="border-brand-500 text-brand-500">
              Send Request
            </Button> */}
          </div>
        );
      default:
        break;
    }
  };
  const columns = [
    {
      title: "Lead and Vehicle",
      render: (text, record) => (
        <div className="flex-col-centered items-start" key={record?.lead?.id}>
          <CustomButton
            type="link"
            btnText={record?.lead?.fullName}
            className="!pl-0 !h-auto !whitespace-normal !text-left [&>span]:!whitespace-normal [&>span]:!break-words"
            handleClick={() => {
              // Dashboard origin — wipe every Activity Center filter so the
              // target lead opens on a clean slate (otherwise stale
              // user/team/date/quickFilter state in redux carries over).
              dispatch(resetLeadFilters());
              redirectToLead(record?.lead?.id);
            }}
          />
          <span>
            {(record?.lead?.vehicleOfInterest || []).map((item, idx) => (
              <span key={idx}>{parseVehicle(item)}</span>
            ))}
          </span>
        </div>
      ),
    },
    {
      title: "Sold By",
      render: (text, record, index) => (
        <div className="flex-col-centered items-start" key={index}>
          <CheckoutReportUserLink userId={record?.user?.id} name={record.user.fullName} />
          <span>{record.soldDate && dayjs(record.soldDate).format("MMM DD, YYYY")}</span>
        </div>
      ),
    },
    {
      title: "Status",
      render: (text, record, index) => <span key={index}>{reviewStatusRender(record.status)}</span>,
    },
  ];
  return (
    <div>
      <MetricHeader title="Review Status" metricDetails={REVIEW_STATUS_INFO}>
        <Select
          placeholder="Review Status"
          options={[
            { label: "Review Completed", value: "review_completed" },
            { label: "Review request not sent", value: "invite_not_sent" },
            { label: "Review sent", value: "invites_sent" },
          ]}
          onChange={setreviewStatus}
          allowClear
          className="min-w-56"
        />
      </MetricHeader>
      <CustomTable
        bordered
        columns={columns}
        className="mt-2"
        data={reviewStatusData.data}
        loading={reviewStatusAnalysisLoader}
        scroll={{ y: 300 }}
        pagination={buildTablePagination({
          total: reviewStatusData.count,
          page: params.page,
          pageSize: params.pageSize,
          setParamsFn: setparams,
        })}
      />
    </div>
  );
};
